import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { showNotification, isTabVisible, playNotificationSound } from '../utils/browserNotifications';
import { useParams, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { AuthContext } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import ThreadView from '../components/ThreadView';
import MessageActions from '../components/MessageActions';
import PinnedMessages from '../components/PinnedMessages';
import MessageEditor from '../components/MessageEditor';
import ForwardMessage from '../components/ForwardMessage';
import './Chat.css';
import FileUpload from '../components/FileUpload';
import RoomManagement from '../components/RoomManagement';
import DeliveryStatus from '../components/DeliveryStatus';

// Simple throttle function
const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  return function (...args) {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
const generateClientMsgId = () => `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper function to get file type icon
const getFileTypeIcon = (mimetype, filename) => {
  if (!mimetype && !filename) return '📎';
  
  const type = mimetype || '';
  const name = filename || '';
  
  if (type.startsWith('image/')) return '🖼️';
  if (type.startsWith('video/')) return '🎥';
  if (type.startsWith('audio/')) return '🎵';
  if (type.includes('pdf')) return '📄';
  if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return '📝';
  if (type.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx')) return '📊';
  if (type.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx')) return '📋';
  if (type.includes('zip') || type.includes('rar') || name.endsWith('.zip') || name.endsWith('.rar')) return '📦';
  if (type.includes('text') || name.endsWith('.txt')) return '📄';
  
  return '📎';
};

// Helper function to render attachment preview
const getAttachmentPreview = (attachment) => {
  const mimetype = attachment.mimetype || attachment.type;
  const filename = attachment.originalName || attachment.name;
  
  if (mimetype?.startsWith('image/')) {
    return (
      <div className="file-attachment-image">
        <img 
          src={attachment.preview || `${API_BASE}${attachment.path}`} 
          alt={filename}
          className="attachment-preview-img"
          onClick={async () => {
            try {
              // Track file access in backend
              await fetch(`${API_BASE}/api/upload/chat/${attachment.filename}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
            } catch (error) {
              console.warn('Could not track file access:', error);
            }
            // Open image in new tab for better viewing
            window.open(attachment.preview || `${API_BASE}${attachment.path}`, '_blank');
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className="file-attachment-icon" style={{ display: 'none' }}>
          {getFileTypeIcon(mimetype, filename)}
        </div>
      </div>
    );
  }
  
  return (
    <div className="file-attachment-icon">
      {getFileTypeIcon(mimetype, filename)}
    </div>
  );
};

// Helper function to check if file has been downloaded
const isFileDownloaded = (attachment) => {
  // Check if the file exists in localStorage as downloaded
  const downloadedFiles = JSON.parse(localStorage.getItem('downloadedFiles') || '{}');
  return downloadedFiles[attachment.path] || false;
};

// Helper function to mark file as downloaded
const markFileAsDownloaded = (attachment) => {
  const downloadedFiles = JSON.parse(localStorage.getItem('downloadedFiles') || '{}');
  downloadedFiles[attachment.path] = true;
  localStorage.setItem('downloadedFiles', JSON.stringify(downloadedFiles));
};

// Helper function to clear download history
const clearDownloadHistory = () => {
  localStorage.removeItem('downloadedFiles');
  console.log('🗑️ Download history cleared');
};

// Helper function to get attachment action button
  const getAttachmentActionButton = (attachment) => {
    const isDownloaded = isFileDownloaded(attachment);
    const buttonText = isDownloaded ? 'Open' : 'Download';
    const buttonClass = isDownloaded ? 'file-attachment-open' : 'file-attachment-download';
    
    const handleFileAccess = async () => {
      try {
        // Track file access in backend
        await fetch(`${API_BASE}/api/upload/chat/${attachment.filename}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.warn('Could not track file access:', error);
        // Don't fail the file access if tracking fails
      }
    };
    
    if (isDownloaded) {
      return (
        <button 
          className={buttonClass}
          onClick={async () => {
            await handleFileAccess();
            // Open file in new tab
            window.open(`${API_BASE}${attachment.path}`, '_blank');
          }}
          title="Open file in new tab"
        >
          {buttonText}
        </button>
      );
    } else {
      return (
        <a 
          href={attachment.preview || `${API_BASE}${attachment.path}`} 
          download={attachment.originalName || attachment.name}
          className={buttonClass}
          target="_blank"
          rel="noopener noreferrer"
          onClick={async () => {
            await handleFileAccess();
            // Mark as downloaded when download starts
            markFileAsDownloaded(attachment);
          }}
          title="Download file"
        >
          {buttonText}
        </a>
      );
    }
  };



// Helper function to normalize room IDs
const normalizeRoomId = (id) => {
  return id ? String(id) : null;
};

// Helper function to get unread count for a room
const getUnreadCount = (unreadMessages, roomId) => {
  if (!roomId || !unreadMessages) return 0;
  
  // Try both possible ID formats
  const normalizedId = normalizeRoomId(roomId);
  return unreadMessages[normalizedId] || unreadMessages[roomId] || 0;
};

// Helper function to format dates safely
const formatChatTime = (dateString) => {
  // If no date provided, show current time as fallback
  if (!dateString) {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  try {
    const date = new Date(dateString);
    
    // If invalid date, try to extract timestamp or use fallback
    if (isNaN(date.getTime())) {
      // Try parsing as timestamp
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        const timestampDate = new Date(timestamp);
        if (!isNaN(timestampDate.getTime())) {
          return timestampDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }
      // Final fallback - show "Now"
      return 'Now';
    }
    
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffInDays < 7) {
      // This week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Older - show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  } catch (error) {
    console.warn('Error formatting date:', dateString, error);
    // Always show something - fallback to "Now"
    return 'Now';
  }
};

// Helper function to format message preview
const formatMessagePreview = (room, currentUserId) => {
  if (!room.lastMessage) {
    return 'No messages yet';
  }

  const message = room.lastMessage;
  
  // If the last message is deleted, show appropriate message
  if (message.isDeleted) {
    const isOwnMessage = message.sender?._id === currentUserId;
    const senderName = isOwnMessage ? 'You' : (message.sender?.name || 'Unknown');
    return (
      <span>
        <span className={isOwnMessage ? 'message-preview-own' : ''}>{senderName}:</span>{' '}
        <span className="deleted-message-preview">🗑️ Message was deleted</span>
      </span>
    );
  }
  
  // If the last message is a system message, show it differently
  if (message.isSystemMessage) {
    return (
      <span className="system-message-preview">
        {message.content}
      </span>
    );
  }
  
  const isOwnMessage = message.sender?._id === currentUserId;
  const senderName = isOwnMessage ? 'You' : (message.sender?.name || 'Unknown');
  
  // Handle different message types
  if (message.attachments && message.attachments.length > 0) {
    const attachmentCount = message.attachments.length;
    const attachmentType = message.attachments[0].mimetype?.startsWith('image/') ? 'image' : 'file';
    const attachmentText = attachmentCount > 1 ? `${attachmentCount} ${attachmentType}s` : attachmentType;
    
    return (
      <span>
        <span className={isOwnMessage ? 'message-preview-own' : ''}>{senderName}:</span>{' '}
        <span className="message-preview-attachment">
          {attachmentType === 'image' ? '📷' : '📎'} {attachmentText}
        </span>
      </span>
    );
  } else if (message.content) {
    // Truncate long messages
    const content = message.content.length > 50 ? message.content.substring(0, 50) + '...' : message.content;
    
    return (
      <span>
        <span className={isOwnMessage ? 'message-preview-own' : ''}>{senderName}:</span>{' '}
        <span>{content}</span>
      </span>
    );
  } else {
    return (
      <span>
        <span className={isOwnMessage ? 'message-preview-own' : ''}>{senderName}:</span>{' '}
        <span>Message</span>
      </span>
    );
  }
};

export default function Chat() {
  const { user, token } = useContext(AuthContext);
  const { updateTotalUnreadCount } = useChat();
  const { roomId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('all');
  const [favoriteChats, setFavoriteChats] = useState(new Set());
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const currentRoomRef = useRef(null);
  useEffect(() => { currentRoomRef.current = currentRoom; }, [currentRoom]);
  // DMs are now handled as regular rooms with type 'direct'
  // const [currentDM, setCurrentDM] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isSocketAuthed, setIsSocketAuthed] = useState(false);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showStartNewChat, setShowStartNewChat] = useState(false);
  const [showRoomManagement, setShowRoomManagement] = useState(false);
  const [roomUpdateCounter, setRoomUpdateCounter] = useState(0);
  const [roomMembers, setRoomMembers] = useState([]);
  const [isRoomAdmin, setIsRoomAdmin] = useState(false);
  const messagesEndRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // @ Mentions functionality state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  
  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [mobileView, setMobileView] = useState('chat'); // 'chat', 'sidebar', 'search'
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isInsertingMention, setIsInsertingMention] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  
  // Reply functionality
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState(null);

  // Threading functionality
  const [showThread, setShowThread] = useState(false);
  const [currentThreadMessage, setCurrentThreadMessage] = useState(null);
  const [threadCounts, setThreadCounts] = useState({});

  // Pinning functionality
  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  const [pinnedMessageIds, setPinnedMessageIds] = useState(new Set());

  // Message editing functionality
  const [editingMessageId, setEditingMessageId] = useState(null);

  // Message forwarding functionality
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [messageToForward, setMessageToForward] = useState(null);

  // Notification system
  const [notification, setNotification] = useState(null);
  
  // Unread message counts - using global context only
  const seenMessageIdsRef = useRef(new Set());
  const readMessageIdsRef = useRef(new Set());
  
  // Get unread messages from global context
  const { unreadMessages, updateUnreadMessages, fetchUnreadCounts: contextFetchUnreadCounts, incrementUnread } = useChat();

  // Helper function to toggle favorite status
  const toggleFavorite = (chatId) => {
    setFavoriteChats(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(chatId)) {
        newFavorites.delete(chatId);
      } else {
        newFavorites.add(chatId);
      }
      // Save to localStorage
      localStorage.setItem('favoriteChats', JSON.stringify([...newFavorites]));
      return newFavorites;
    });
  };

  // Function to emit read receipt events for specific messages
  const emitReadReceipts = useCallback((messageIds) => {
    if (!socket || !currentRoom || !messageIds || messageIds.length === 0) return;

    const currentUserId = user._id || user.id;
    const unreadMessageIds = messageIds.filter(id => !readMessageIdsRef.current.has(id));

    if (unreadMessageIds.length === 0) return;

    // Mark messages as read locally
    unreadMessageIds.forEach(id => readMessageIdsRef.current.add(id));

    // Emit read events for each message
    unreadMessageIds.forEach(messageId => {
      socket.emit('message:read', {
        messageId: messageId,
        roomId: currentRoom._id,
        userId: currentUserId,
        readAt: new Date().toISOString()
      });
    });

    console.log('📖 Emitted read receipts for:', unreadMessageIds);
  }, [socket, currentRoom, user]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favoriteChats');
    if (savedFavorites) {
      setFavoriteChats(new Set(JSON.parse(savedFavorites)));
    }
  }, []);

  // Handle joining via invite link
  useEffect(() => {
    if (roomId && location.pathname.includes('/join/')) {
      const joinRoom = async () => {
        try {
          const response = await fetch(`${API_BASE}/api/chat/rooms/${roomId}/join`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            showNotification(`🎉 Successfully joined "${result.room.name}"!`, 'success');
            // Navigate to the chat page
            window.history.replaceState({}, '', '/chat');
          } else {
            const error = await response.json().catch(() => ({}));
            showNotification(error.message || 'Failed to join room', 'error');
          }
        } catch (error) {
          showNotification('Failed to join room', 'error');
        }
      };
      
      joinRoom();
    }
  }, [roomId, location.pathname, token]);

  // Fetch contacts when contact picker is opened
  useEffect(() => {
    if (showContactPicker && availableContacts.length === 0) {
      fetchAvailableContacts();
    }
  }, [showContactPicker]);

  // Helper function to filter chats based on active tab
  const filterChats = (chats) => {
    let filteredChats;
    switch (activeTab) {
      case 'all':
        filteredChats = chats;
        break;
      case 'unread':
        filteredChats = chats.filter(chat => getUnreadCount(unreadMessages, chat._id) > 0);
        break;
      case 'favorites':
        filteredChats = chats.filter(chat => favoriteChats.has(chat._id));
        break;
      case 'groups':
        filteredChats = chats.filter(chat => chat.type === 'public' || chat.type === 'private');
        break;
      default:
        filteredChats = chats;
    }
    
    // Sort by latest message timestamp (most recent first)
    return filteredChats.sort((a, b) => {
      const aTime = a.lastMessage?.createdAt || a.updatedAt || a.createdAt;
      const bTime = b.lastMessage?.createdAt || b.updatedAt || b.createdAt;
      return new Date(bTime) - new Date(aTime);
    });
  };

  // Fetch initial unread counts when component mounts
  useEffect(() => {
    if (contextFetchUnreadCounts) {
      console.log('📊 Fetching initial unread counts...');
      contextFetchUnreadCounts();
    }
  }, [contextFetchUnreadCounts]);

  // Debug: Log unreadMessages changes
  useEffect(() => {
    console.log('📊 Chat Component: unreadMessages changed:', unreadMessages);
  }, [unreadMessages]);

  // Mark messages as read when they come into view
  useEffect(() => {
    if (!currentRoom || messages.length === 0) return;

    const currentUserId = user._id || user.id;
    
    // Get messages from other users that haven't been read yet
    const unreadMessages = messages.filter(msg => {
      const senderId = msg.sender?._id || msg.sender?.id;
      const isFromOtherUser = senderId && senderId.toString() !== currentUserId.toString();
      const notReadYet = !readMessageIdsRef.current.has(msg._id);
      return isFromOtherUser && notReadYet;
    });

    if (unreadMessages.length > 0) {
      // Emit read receipts after a short delay to ensure they're visible
      const timeoutId = setTimeout(() => {
        const messageIds = unreadMessages.map(msg => msg._id);
        emitReadReceipts(messageIds);
      }, 1000); // 1 second delay

      return () => clearTimeout(timeoutId);
    }
  }, [messages, currentRoom, user, emitReadReceipts]);

  // Debug: Test function to manually update unread count
  const testUnreadCount = () => {
    console.log('🧪 Testing unread count update...');
    const testRoomId = 'test-room-123';
    const testCounts = {
      ...unreadMessages,
      [testRoomId]: (unreadMessages[testRoomId] || 0) + 1
    };
    updateUnreadMessages(testCounts);
  };

  // Add test function to window for debugging
  useEffect(() => {
    window.testUnreadCount = testUnreadCount;
    console.log('🧪 Test function available: call window.testUnreadCount() to test unread count updates');
  }, [unreadMessages, updateUnreadMessages]);
  


  
  // Delivery status tracking
  const [deliveryStatus, setDeliveryStatus] = useState({});
  const [messageStates, setMessageStates] = useState({});

  // Note: Total unread count is now managed by ChatContext globally

  // Debug functions removed to prevent flickering
  
  // Notification sound function
  const playNotificationSound = () => {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  // Show browser notification
  const showBrowserNotification = (message) => {
    try {
      // Check if Notification is supported and permission is granted
      if (!('Notification' in window) || Notification.permission !== 'granted') {
        console.log('Notifications not supported or permission not granted');
        return;
      }

      // Check if we're in a secure context (required for notifications)
      if (!window.isSecureContext) {
        console.log('Notifications require a secure context (HTTPS)');
        return;
      }

      const notification = new Notification('New Message', {
        body: `${message.sender?.name || 'Someone'}: ${message.content || message.body || ''}`,
        icon: '/favicon.ico',
        tag: message.roomId
      });
      
      notification.onclick = () => {
        try {
          window.focus();
          notification.close();
        } catch (e) {
          console.log('Error handling notification click:', e);
        }
      };
    } catch (error) {
      console.error('Error creating notification:', error);
      // Fallback: just log the message instead of showing notification
      console.log(`Chat notification: ${message.sender?.name || 'Someone'}: ${message.body}`);
    }
  };

  // Request notification permission on component mount
  useEffect(() => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(error => {
          console.error('Error requesting notification permission:', error);
        });
      }
    } catch (error) {
      console.error('Error checking notification permission:', error);
    }
  }, []);
  
  // Typing indicators - track per room: { roomId: { userId: { name, timestamp } } }
  const [typingUsersByRoom, setTypingUsersByRoom] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Monitor users state changes for debugging
  useEffect(() => {
    console.log('🔍 Users state changed:', users);
  }, [users]);

  // Monitor user context and token for debugging
  useEffect(() => {
    console.log('🔍 User context changed:', { user, token, hasUser: !!user, hasToken: !!token });
  }, [user, token]);

  useEffect(() => {
    if (!Array.isArray(messages)) {
      setMessages([]);
    }
  }, [messages]);

  useEffect(() => {
    console.log('🔌 Initializing socket connection to:', API_BASE);
    
    // In production, connect to same origin; in dev, use API_BASE
    const socketUrl = process.env.NODE_ENV === 'production' ? '/' : API_BASE;
    const newSocket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      // The server verifies this JWT in the handshake; read it on every (re)connect
      auth: cb => cb({ token: localStorage.getItem('token') }),
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('✅ Connected to socket server:', newSocket.id);
      console.log('🔍 Socket connection established for user:', user.name, 'ID:', user._id || user.id);
      setIsConnected(true);
      
      // Authenticate with user info
      const authPayload = {
        userId: user._id || user.id,
        name: user.name,
        role: user.role
      };
      console.log('🔐 Emitting socket authenticate:', authPayload);
      newSocket.emit('authenticate', authPayload);
    });

    newSocket.on('authenticated', (data) => {
      console.log('✅ Socket authenticated:', data);
      setIsSocketAuthed(true);
      
      // Test socket connection
      console.log('🧪 Testing socket connection...');
      newSocket.emit('test', { message: 'Hello from frontend!' });
    });


    newSocket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setIsConnected(false);
      setIsSocketAuthed(false);
    });


    // Room join acknowledgement
    newSocket.on('room:joined', (data) => {
      console.log('🚪 Socket: Joined room:', data);
    });


    // Listen to the correct message broadcast event from server
    newSocket.on('message:new', (data) => {
      console.log('📨 FRONTEND: Received message:new event:', data);
      console.log('📨 FRONTEND: Socket connected:', newSocket.connected);
      console.log('📨 FRONTEND: Current user:', user);
      console.log('📨 FRONTEND: Message sender:', data.sender);
      console.log('📨 FRONTEND: Socket authenticated:', isSocketAuthed);
      
      // De-dup: ignore if we've already seen this message id or client_msg_id
      const dedupeKey = data.ackId || data._id || data.client_msg_id || `${data.roomId}:${data.seq}`;
      if (dedupeKey && seenMessageIdsRef.current.has(dedupeKey)) {
        console.log('📨 Duplicate message ignored:', dedupeKey);
        return;
      }
      if (dedupeKey) seenMessageIdsRef.current.add(dedupeKey);

      // If optimistic message exists for this client_msg_id, replace it
      if (data.client_msg_id) {
        setMessages(prev => {
          const idx = prev.findIndex(m => m.client_msg_id === data.client_msg_id || m._id === data.client_msg_id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = { ...prev[idx], ...data };
            return next;
          }
          return [...prev, data];
        });
      } else {
        // Always add new messages to the messages state
      setMessages(prev => [...prev, data]);
      }
      
      // Update the rooms list to reflect the new last message
      setRooms(prev => prev.map(room => {
        if (room._id === data.roomId) {
          return {
            ...room,
            lastMessage: data,
            updatedAt: data.createdAt || new Date().toISOString()
          };
        }
        return room;
      }));
      
      // Update thread counts if this is a thread reply
      if (data.replyTo) {
        console.log('🧵 [THREAD] Reply detected, updating thread count for parent:', data.replyTo);
        setThreadCounts(prev => ({
          ...prev,
          [data.replyTo]: {
            ...prev[data.replyTo],
            replyCount: (prev[data.replyTo]?.replyCount || 0) + 1
          }
        }));
      }

      // Unread + notifications for standard message delivery
      try {
        const currentUserId = user._id || user.id;
        const senderId = data.sender?._id || data.sender?.id;
        const isFromOtherUser = senderId && String(senderId) !== String(currentUserId);
        const isCurrentRoom = currentRoom?._id && String(currentRoom._id) === String(data.roomId);

        // Increment unread only when message is from other user and not in current room
        if (isFromOtherUser && !isCurrentRoom) {
          incrementUnread(String(data.roomId));
        }

        // Show notifications when tab hidden or other room
        if (isFromOtherUser && (!isCurrentRoom || !isTabVisible())) {
          try { playNotificationSound(); } catch {}
          try {
            showNotification('New message', {
              body: data.content || 'You have a new message',
              tag: 'nesim-chat-inline'
            });
          } catch {}
        }
      } catch (e) {
        console.warn('Unread/notify handling failed for message:new', e);
      }
    });

    // Listen for thread reply events (when someone replies to a message)
    newSocket.on('thread:reply', (data) => {
      console.log('🧵 [THREAD] Thread reply event received:', data);
      
      // Add the reply to messages if not already there
      const replyId = data.reply?._id || data.reply?.ackId;
      setMessages(prev => {
        // Check if reply already exists
        const exists = prev.some(m => 
          m._id === replyId || m.ackId === replyId || m.client_msg_id === data.reply?.client_msg_id
        );
        
        if (exists) {
          console.log('🧵 [THREAD] Reply already in messages, skipping');
          return prev;
        }
        
        console.log('🧵 [THREAD] Adding thread reply to messages');
        return [...prev, data.reply];
      });
      
      // Update thread count for parent message
      if (data.parentMessageId) {
        console.log('🧵 [THREAD] Updating thread count for parent:', data.parentMessageId);
        setThreadCounts(prev => ({
          ...prev,
          [data.parentMessageId]: {
            ...prev[data.parentMessageId],
            replyCount: (prev[data.parentMessageId]?.replyCount || 0) + 1
          }
        }));
      }
      
      // Check if message is from other user
      const currentUserId = user._id || user.id;
      const senderId = data.sender?._id;
      const isFromOtherUser = senderId && senderId.toString() !== currentUserId.toString();
      const isCurrentRoom = currentRoom?._id && String(currentRoom._id) === String(data.roomId);
      
      // Increment unread only when the message is from other users AND not in the current room
      if (isFromOtherUser && !isCurrentRoom) {
        const normalizedRoomId = normalizeRoomId(data.roomId);
        const newCounts = {
          ...unreadMessages,
          [normalizedRoomId]: (unreadMessages[normalizedRoomId] || 0) + 1
        };
        
        console.log('📨 Chat: New message from other user in different room, updating counts for room:', normalizedRoomId, 'count:', newCounts[normalizedRoomId]);
        updateUnreadMessages(newCounts);
      } else {
        console.log('📨 Chat: Message from self or current room, not updating unread count');
      }
      
      const isNotCurrentRoom = !isCurrentRoom;
      console.log('🔔 Notification check:', {
        isFromOtherUser,
        isNotCurrentRoom,
        senderId: senderId,
        currentUserId: currentUserId,
        senderIdString: senderId?.toString(),
        currentUserIdString: currentUserId?.toString(),
        currentRoomId: currentRoom?._id,
        messageRoomId: data.roomId
      });
      
      // Show notifications when tab is hidden or room is not current and from other users
      if (isFromOtherUser && (!isCurrentRoom || !isTabVisible())) {
        console.log('🔔 Showing notification for message from other user');
        try { playNotificationSound(); } catch {}
        try {
          showNotification('New message', {
            body: data.content || 'You have a new message',
            tag: 'nesim-chat-inline'
          });
        } catch {}
      } else {
        console.log('🔔 Not showing notification - either from self or current room');
      }
    });

    // Handle server acknowledgment for sent messages
    newSocket.on('message:server-ack', (data) => {
      setMessageStates(prev => ({
        ...prev,
        [data.client_msg_id]: 'sent'
      }));
      
      // Update the message delivery status
      setMessages(prev => prev.map(msg => {
        if (msg.client_msg_id === data.client_msg_id) {
          return {
            ...msg,
            delivery: {
              ...msg.delivery,
              sentAt: data.sentAt
            }
          };
        }
        return msg;
      }));
    });

    // Handle message delivered events
    newSocket.on('message:delivered', (data) => {
      setMessages(prev => prev.map(msg => {
        if (msg._id === data.messageId) {
          const updatedDelivery = {
            ...msg.delivery,
            deliveredTo: [
              ...(msg.delivery?.deliveredTo || []),
              { user: data.userId, deliveredAt: data.deliveredAt }
            ]
          };
          return { ...msg, delivery: updatedDelivery };
        }
        return msg;
      }));
    });

    // Handle bulk messages read events - refresh messages to get updated read status
    newSocket.on('messages:read', (data) => {
      if (data.roomId === currentRoom?._id && data.userId !== (user._id || user.id)) {
        // Only refresh if we're in the same room and it's not our own read action
        setTimeout(() => {
          if (currentRoom?._id === data.roomId) {
            fetchMessages(currentRoom._id);
          }
        }, 100); // Small delay to ensure database is updated
      }
    });


    // Handle message pinned events
    newSocket.on('message:pinned', (data) => {
      if (data.roomId === currentRoom?._id) {
        setPinnedMessageIds(prev => new Set([...prev, data.messageId]));
        showNotification(`Message pinned by ${data.pinnedBy?.name || 'admin'}`, 'info');
      }
    });

    // Handle message unpinned events
    newSocket.on('message:unpinned', (data) => {
      if (data.roomId === currentRoom?._id) {
        setPinnedMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.messageId);
          return newSet;
        });
        showNotification(`Message unpinned by ${data.unpinnedBy?.name || 'admin'}`, 'info');
      }
    });

    // Handle message edited events - flattened payload like message:new
    newSocket.on('message:edited', (msg) => {
      console.log('🔧 [EDIT EVENT] Received flattened message:edited:', {
        _id: msg._id,
        roomId: msg.roomId,
        content: msg.content,
        editedBy: msg.editedBy?.name,
        editedAt: msg.editedAt
      });
      
      // Ensure meta.editHistory exists for "Edited" tag
      if (!msg.meta) msg.meta = {};
      if (!Array.isArray(msg.meta.editHistory)) {
        msg.meta.editHistory = [{ 
          timestamp: msg.editedAt, 
          content: msg.content 
        }];
      }
      
      // Update messages by replacing the old one (try _id, fall back to seq)
      setMessages(prev => {
        // Try by _id first
        let idx = prev.findIndex(m => String(m._id) === String(msg._id));
        
        // Fall back to seq if available
        if (idx === -1 && msg.seq != null) {
          idx = prev.findIndex(m => Number(m.seq) === Number(msg.seq));
        }
        
        // Fall back to ackId if available
        if (idx === -1 && msg.ackId) {
          idx = prev.findIndex(m => String(m.ackId) === String(msg.ackId));
        }
        
        if (idx === -1) {
          console.log('🔧 [EDIT EVENT] Message not in window, ignoring');
          return prev;
        }
        
        console.log('🔧 [EDIT EVENT] Found message at index', idx, 'replacing:', {
          oldContent: prev[idx].content,
          newContent: msg.content
        });
        
        const next = [...prev];
        // Preserve local UI flags, merge in edited message
        next[idx] = { ...next[idx], ...msg, isEdited: true };
        
        console.log('🔧 [EDIT EVENT] Message updated successfully');
        return next;
      });
      
      // Don't show notification if current user edited the message
      if (msg.editedBy?._id !== (user._id || user.id)) {
        console.log('🔧 [EDIT EVENT] Showing notification for edit by other user');
        showNotification(`Message edited by ${msg.editedBy?.name || 'user'}`, 'info');
      } else {
        console.log('🔧 [EDIT EVENT] Current user edited, skipping notification');
      }
      
      // Update rooms if this was the last message
      setRooms(prev => prev.map(room => {
        if (String(room._id) === String(msg.roomId)) {
          const isLastMessage = room.lastMessage && (
            String(room.lastMessage._id) === String(msg._id) ||
            (room.lastMessage.ackId && msg.ackId && String(room.lastMessage.ackId) === String(msg.ackId)) ||
            (room.lastMessage.seq && msg.seq && Number(room.lastMessage.seq) === Number(msg.seq))
          );
          
          if (isLastMessage) {
            console.log('🔧 [EDIT EVENT] Updating room last message');
            return {
              ...room,
              lastMessage: { ...msg, isEdited: true },
              updatedAt: msg.editedAt
            };
          }
        }
        return room;
      }));
    });

    // Handle message deleted events
    newSocket.on('message:deleted', (data) => {
      if (data.roomId === currentRoom?._id) {
        // Update message to show as deleted instead of removing it
        setMessages(prev => prev.map(msg => 
          msg._id === data.messageId 
            ? { ...msg, isDeleted: true, deletedAt: data.deletedAt, deletedBy: data.deletedBy._id }
            : msg
        ));
        // Don't show notification if current user deleted the message
        if (data.deletedBy._id !== (user._id || user.id)) {
          showNotification(`Message deleted by ${data.deletedBy?.name || 'user'}`, 'info');
        }
      }
      
      // Update rooms if this was the last message
      setRooms(prev => prev.map(room => {
        if (room._id === data.roomId && room.lastMessage?._id === data.messageId) {
          return {
            ...room,
            lastMessage: { ...room.lastMessage, isDeleted: true, deletedAt: data.deletedAt, deletedBy: data.deletedBy._id },
            updatedAt: data.deletedAt || new Date().toISOString()
          };
        }
        return room;
      }));
    });

    // Handle typing start events (track across ALL rooms for chat list)
    newSocket.on('typing:start', (data) => {
      console.log('⌨️ [TYPING] Typing start event received:', data);
      // Don't show our own typing
      if (data.userId === (user?._id || user?.id)) return;
      
      setTypingUsersByRoom(prev => ({
        ...prev,
        [data.roomId]: {
          ...prev[data.roomId],
          [data.userId]: {
            name: data.userName,
            timestamp: data.timestamp
          }
        }
      }));
      console.log('⌨️ [TYPING] Updated typing users for room:', data.roomId);
    });

    // Handle typing stop events (track across ALL rooms)
    newSocket.on('typing:stop', (data) => {
      console.log('⌨️ [TYPING] Typing stop event received:', data);
      // Don't track our own typing
      if (data.userId === (user?._id || user?.id)) return;
      
      setTypingUsersByRoom(prev => {
        const updated = { ...prev };
        if (updated[data.roomId]) {
          delete updated[data.roomId][data.userId];
          // If no more users typing in this room, remove the room entry
          if (Object.keys(updated[data.roomId]).length === 0) {
            delete updated[data.roomId];
          }
        }
        return updated;
      });
      console.log('⌨️ [TYPING] Removed typing user from room:', data.roomId);
    });

    // Handle room member added events
    newSocket.on('room:member_added', (data) => {
      console.log('🏠 Member added to room:', data);
      console.log('🏠 Current room ID:', currentRoom?._id);
      console.log('🏠 Event room ID:', data.roomId);
      console.log('🏠 Is current room?', currentRoom && currentRoom._id === data.roomId);
      console.log('🏠 Socket connected?', newSocket.connected);
      console.log('🏠 Socket ID:', newSocket.id);
      
      // Forced test message removed - using real socket events
      const { roomId, room: updatedRoom, addedUser, addedBy } = data;
      
      // Update rooms list with new member data, or add the room if it's new
      setRooms(prev => {
        const existingRoom = prev.find(room => room._id === roomId);
        if (existingRoom) {
          return prev.map(room => room._id === roomId ? updatedRoom : room);
        } else {
          // If the current user was added to a room they don't have, add it
          if (addedUser && addedUser._id === (user._id || user.id)) {
            return [...prev, updatedRoom];
          }
          return prev;
        }
      });
      
      // Show notification if it's not the current user being added
      if (addedUser && addedUser._id !== (user._id || user.id)) {
        showNotification(`🎉 ${addedUser.name || addedUser.email} joined the room${addedBy ? ` (added by ${addedBy.name})` : ''}`, 'success');
      }
      
      // If this is the current room, update room members
      if (currentRoom && currentRoom._id === roomId) {
        setRoomMembers(updatedRoom.members);
      }
      
      // If the current user was added to a new room, add it to their room list
      if (addedUser && addedUser._id === (user._id || user.id)) {
        showNotification(`✨ You were added to "${updatedRoom.name}"${addedBy ? ` by ${addedBy.name}` : ''}`, 'success');
      }
    });

    // Handle message pinned events
    // Duplicate pin/unpin listeners removed - using the first ones above

    // Handle room member removed events
    newSocket.on('room:member_removed', (data) => {
      console.log('🏠 Chat.js - Member removed from room:', data);
      console.log('🏠 Current room ID:', currentRoom?._id);
      console.log('🏠 Event room ID:', data.roomId);
      console.log('🏠 Is current room?', currentRoom && currentRoom._id === data.roomId);
      
      // Forced test message removed - using real socket events
      console.log('🏠 Chat.js - Socket event data details:', {
        roomId: data.roomId,
        updatedRoomMembers: data.room?.members?.length,
        removedUserId: data.removedUserId,
        removedUser: data.removedUser?.name || data.removedUser?.email,
        removedBy: data.removedBy?.name || data.removedBy?.email
      });
      const { roomId, room: updatedRoom, removedUserId, removedUser, removedBy } = data;
      
      console.log('🏠 Chat.js - Member removal details:', {
        roomId,
        removedUserId,
        removedUser: removedUser?.name || removedUser?.email,
        currentRoomId: currentRoom?._id,
        isCurrentRoom: currentRoom && currentRoom._id === roomId,
        updatedMemberCount: updatedRoom?.members?.length,
        updatedMembers: updatedRoom?.members?.map(m => m.name || m.email)
      });
      
      // Update rooms list with updated member data
      setRooms(prev => {
        console.log('🏠 Chat.js - Updating rooms list');
        return prev.map(room => 
          room._id === roomId ? updatedRoom : room
        );
      });
      
      // Show notification if someone else was removed
      if (removedUserId !== (user._id || user.id)) {
        showNotification(`👋 ${removedUser?.name || removedUser?.email || 'A user'} was removed from the room${removedBy ? ` by ${removedBy.name}` : ''}`, 'warning');
      }
      
      // If this is the current room, update room members and add system message
      if (currentRoom && currentRoom._id === roomId) {
        console.log('🏠 Chat.js - Updating room members for current room');
        console.log('🏠 Previous roomMembers count:', roomMembers?.length);
        console.log('🏠 New roomMembers count:', updatedRoom.members?.length);
        setRoomMembers(updatedRoom.members);
        
        
        // If RoomManagement modal is open, force it to update
        if (showRoomManagement && selectedRoom?._id === roomId) {
          setRoomUpdateCounter(prev => prev + 1);
        }
      }
    });

    // Handle being kicked from a room
    newSocket.on('room:kicked', (data) => {
      console.log('🚪 Kicked from room:', data);
      const { roomId, removedBy } = data;
      
      // Find room name before removing it
      const kickedRoom = rooms.find(room => room._id === roomId);
      const roomName = kickedRoom?.name || 'a room';
      
      // Remove the room from the user's room list
      setRooms(prev => prev.filter(room => room._id !== roomId));
      
      // If this was the current room, clear it
      if (currentRoom && currentRoom._id === roomId) {
        setCurrentRoom(null);
        setMessages([]);
        showNotification(`🚫 You were removed from "${roomName}"${removedBy ? ` by ${removedBy.name}` : ''}`, 'error');
      } else {
        showNotification(`🚫 You were removed from "${roomName}"${removedBy ? ` by ${removedBy.name}` : ''}`, 'warning');
      }
    });

    // Handle room deletion
    newSocket.on('room:deleted', (data) => {
      console.log('🗑️ Room deleted:', data);
      const { roomId, roomName, deletedBy } = data;
      
      // Remove the room from our list
      setRooms(prev => prev.filter(room => room._id !== roomId));
      
      // If we were viewing the deleted room, clear it
      if (currentRoom?._id === roomId) {
        setCurrentRoom(null);
        setMessages([]);
      }
      
      // Show notification
      showNotification(`Room "${roomName || 'Unknown'}" has been deleted${deletedBy ? ` by ${deletedBy.name}` : ''}`, 'info');
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [API_BASE]);

  // Authenticate socket once connected and user available
  useEffect(() => {
    if (socket && isConnected && user) {
      const authPayload = {
        userId: user.id || user._id,
        name: user.name || user.email,
        role: user.role || 'user'
      };
      console.log('🔐 Emitting socket authenticate:', authPayload);
      socket.emit('authenticate', authPayload);
    }
  }, [socket, isConnected, user]);

    // Auto-join current room after socket is authenticated
    useEffect(() => {
      if (socket && isSocketAuthed && currentRoom?._id) {
        console.log('🚪 Frontend: Joining room:', currentRoom._id);
        console.log('🚪 Frontend: Socket authenticated:', isSocketAuthed);
        console.log('🚪 Frontend: Socket connected:', socket.connected);
        socket.emit('room:join', { roomId: currentRoom._id });
      }
    }, [socket, isSocketAuthed, currentRoom]);

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/chat/rooms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
      } else if (response.status === 401) {
        setError('Please log in to access chat');
      } else {
        setError('Failed to load chat rooms');
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError('Network error - please check your connection');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      console.log('🔍 Fetching users...');
      const response = await fetch(`${API_BASE}/api/chat/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Users fetched:', data);
        const filteredUsers = data.filter(u => u._id !== (user._id || user.id));
        console.log('🔍 Filtered users:', filteredUsers);
        setUsers(filteredUsers);
        console.log('🔍 Users state set to:', filteredUsers);
        setUsersLoaded(true); // Set usersLoaded to true after fetching
      } else {
        console.error('❌ Failed to fetch users:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
    }
  }, [token, user]);


  const fetchMessages = useCallback(async (roomId) => {
    try {
      console.log('🔍 Fetching messages for room:', roomId);
      
      // Check if roomId is valid
      if (!roomId || roomId === 'undefined') {
        console.log('❌ Invalid room ID:', roomId);
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/chat/rooms/${roomId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📨 Messages fetched:', data);
        // Handle the response structure from the backend
        if (data.messages && Array.isArray(data.messages)) {
          setMessages(data.messages);
        } else if (Array.isArray(data)) {
          setMessages(data);
      } else {
          console.log('⚠️ Unexpected response format:', data);
        setMessages([]);
      }
      } else if (response.status === 404) {
        console.log('⚠️ No messages found for room:', roomId);
      setMessages([]);
      } else {
        console.error('❌ Failed to fetch messages:', response.status, response.statusText);
        setMessages([]);
      }
    } catch (error) {
      console.error('❌ Error fetching messages:', error);
      setMessages([]);
    }
  }, [token]);

  const sendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    
      const roomId = currentRoom?._id;
    if (!roomId) {
      console.error('❌ No room selected for message');
      return;
    }

        // Create temporary message for immediate display
    const clientMsgId = generateClientMsgId();
    const tempMessage = {
      _id: Date.now().toString(),
      content: newMessage.trim(),
      sender: user,
      roomId: roomId,
      createdAt: new Date().toISOString(),
      client_msg_id: clientMsgId,
      delivery: {
        sentAt: null,
        deliveredTo: [],
        readBy: []
      },
              attachments: attachments.map(att => ({
          originalName: att.name,
          name: att.name, // Keep for backward compatibility with local display
          size: att.size,
          mimetype: att.type,
          type: att.type, // Keep for backward compatibility with local display
          path: att.preview, // Use preview as temporary path for local display
          preview: att.preview
        }))
    };

    try {
      console.log('📤 Preparing to send message with attachments:', attachments.length);
      
      // Upload files first if there are attachments
      let uploadedAttachments = [];
      if (attachments.length > 0) {
        try {
          console.log('📎 Uploading files...');
          uploadedAttachments = await uploadFiles(attachments);
          console.log('✅ Files uploaded:', uploadedAttachments);
        } catch (uploadError) {
          console.error('❌ File upload failed:', uploadError);
          alert(`File upload failed: ${uploadError.message}. Please try again.`);
          return; // Don't send the message if file upload fails
        }
      }
      
             const messageData = {
         content: newMessage.trim(),
         roomId: roomId,
         attachments: Array.isArray(uploadedAttachments) ? uploadedAttachments : [],
         client_msg_id: clientMsgId,
         replyTo: replyingTo || null
       };

      console.log('📤 Sending message:', messageData);
      
      // Optimistic UI update - show message immediately
      setMessages(prev => [...prev, tempMessage]);
      
      // Update rooms list optimistically for immediate preview update
      setRooms(prev => prev.map(room => {
        if (room._id === roomId) {
          return {
            ...room,
            lastMessage: tempMessage,
            updatedAt: tempMessage.createdAt
          };
        }
        return room;
      }));
      
      // Update thread counts if this is a thread reply
      if (replyingTo) {
        setThreadCounts(prev => ({
          ...prev,
          [replyingTo]: {
            ...prev[replyingTo],
            replyCount: (prev[replyingTo]?.replyCount || 0) + 1
          }
        }));
      }
      
      setNewMessage('');
      setAttachments([]);
      setReplyingTo(null);
      setReplyMessage(null);
    
      // Do NOT emit over socket to avoid duplicate delivery; REST will persist then broadcast
      
      // Send to API
      const response = await fetch(`${API_BASE}/api/chat/rooms/${roomId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });
      
      if (response.ok) {
        console.log('✅ Message sent successfully');
        scrollToBottom();
      } else {
        console.error('❌ Failed to send message:', response.status);
        // Remove temporary message if API call failed
        setMessages(prev => prev.filter(msg => msg._id !== tempMessage._id));
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      // Remove temporary message if there was an error
      setMessages(prev => prev.filter(msg => msg._id !== tempMessage._id));
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // (Reverted) mention auto-scroll removed

  // Threading functions
  const handleReplyInThread = (message) => {
    // Changed to use inline reply like WhatsApp instead of side panel
    handleReply(message);
  };

  const handleCloseThread = () => {
    setShowThread(false);
    setCurrentThreadMessage(null);
  };

  // Handle inline reply (WhatsApp/Telegram/Instagram style)
  const handleReply = (message) => {
    console.log('↩️ [REPLY] Setting up reply to message:', message._id);
    setReplyingTo(message._id);
    setReplyMessage(message);
    // Focus on the message input
    setTimeout(() => {
      const textarea = document.querySelector('.message-input');
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  // Fetch thread counts for messages
  const fetchThreadCounts = useCallback(async (messageIds) => {
    if (!messageIds || messageIds.length === 0 || !currentRoom) return;

    // Only fetch counts for messages that don't already have them
    const messagesNeedingCounts = messageIds.filter(id => !threadCounts[id]);
    
    if (messagesNeedingCounts.length === 0) {
      return; // No new messages need counts
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/chat/rooms/${currentRoom._id}/messages/thread-summaries`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ messageIds: messagesNeedingCounts })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setThreadCounts(prev => ({
          ...prev,
          ...data.threadSummaries
        }));
      }
    } catch (error) {
      console.error('Error fetching thread counts:', error);
    }
  }, [currentRoom, token, threadCounts]);

  // Update thread counts when messages change (debounced to avoid excessive API calls)
  useEffect(() => {
    if (messages.length > 0) {
      const messageIds = messages.map(msg => msg._id);
      
      // Debounce the API call to avoid excessive requests
      const timeoutId = setTimeout(() => {
        fetchThreadCounts(messageIds);
      }, 500); // 500ms delay
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, fetchThreadCounts]);

  // Handle message actions
  const handleForwardMessage = (message) => {
    setMessageToForward(message);
    setShowForwardModal(true);
  };

  const handleCloseForwardModal = () => {
    setShowForwardModal(false);
    setMessageToForward(null);
  };

  const handleForwardComplete = (result) => {
    showNotification(result?.message || 'Message forwarded', result?.type || 'success');
    setShowForwardModal(false);
    setMessageToForward(null);
  };

  const handlePinMessage = (message) => {
    // Optimistically update the pinned state immediately
    const isPinned = pinnedMessageIds.has(message._id);
    
    setPinnedMessageIds(prev => {
      const newSet = new Set(prev);
      if (isPinned) {
        newSet.delete(message._id);
      } else {
        newSet.add(message._id);
      }
      return newSet;
    });
    
    showNotification(`Message ${isPinned ? 'unpinned' : 'pinned'} successfully!`, 'success');
  };

  const handleShowPinnedMessages = () => {
    setShowPinnedMessages(true);
  };

  const handleClosePinnedMessages = () => {
    setShowPinnedMessages(false);
  };

  const handleUnpinFromPanel = (messageId) => {
    // Update the pinnedMessageIds state when unpinned from the pinned messages panel
    setPinnedMessageIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  };

  // Fetch pinned message IDs for current room
  const fetchPinnedMessageIds = useCallback(async () => {
    if (!currentRoom || !token) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/chat/rooms/${currentRoom._id}/pinned`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const pinnedIds = new Set(data.pinnedMessages.map(pin => pin.message._id));
        setPinnedMessageIds(pinnedIds);
      }
    } catch (error) {
      console.error('Error fetching pinned messages:', error);
    }
  }, [currentRoom, token]);

  // Fetch pinned messages when room changes
  useEffect(() => {
    fetchPinnedMessageIds();
  }, [fetchPinnedMessageIds]);

  // Check if current user is admin of the current room
  useEffect(() => {
    if (currentRoom && user) {
      const userId = user._id || user.id;
      // Check if user is admin or creator of the room, or has global admin privileges
      const isRoomSpecificAdmin = currentRoom.admins?.some(admin => 
        (admin._id || admin) === userId
      ) || currentRoom.creator === userId || (currentRoom.creator?._id === userId);
      
      const isGlobalAdmin = user.role === 'super' || user.role === 'admin';
      const isAdmin = isRoomSpecificAdmin || isGlobalAdmin;
      
      console.log('🔐 Room admin check:', {
        userId,
        userRole: user.role,
        roomAdmins: currentRoom.admins,
        roomCreator: currentRoom.creator,
        isRoomSpecificAdmin,
        isGlobalAdmin,
        isAdmin
      });
      
      setIsRoomAdmin(isAdmin);
    } else {
      setIsRoomAdmin(false);
    }
  }, [currentRoom, user]);

  const handleEditMessage = (message) => {
    setEditingMessageId(message._id);
  };

  const handleSaveEdit = async (updatedMessageOrContent) => {
    console.log('🔧 [CHAT] handleSaveEdit called');
    
    // Check if this is called with updated message object (from MessageEditor API call)
    // or if we need to make the API call ourselves
    if (updatedMessageOrContent && updatedMessageOrContent._id) {
      // MessageEditor already called the API, just update state
      console.log('🔧 [CHAT] Received updated message from API:', updatedMessageOrContent);
      const editedAt = updatedMessageOrContent.editedAt || new Date().toISOString();
      setMessages(prev => prev.map(msg => {
        if (msg._id !== updatedMessageOrContent._id) return msg;
        const merged = { ...msg, ...updatedMessageOrContent };
        merged.editedAt = editedAt;
        merged.roomId = msg.roomId;
        return merged;
      }));

      // Update rooms lastMessage if it matches
      setRooms(prev => prev.map(room => {
        if (room.lastMessage?._id === updatedMessageOrContent._id) {
          const mergedLast = { ...room.lastMessage, ...updatedMessageOrContent, editedAt };
          return { ...room, lastMessage: mergedLast };
        }
        return room;
      }));

      setEditingMessageId(null);
      showNotification('Message edited successfully!', 'success');
    } else {
      console.warn('🔧 [CHAT] handleSaveEdit called with invalid data:', updatedMessageOrContent);
      setEditingMessageId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
  };

  const handleDeleteMessage = async (message) => {
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE}/api/chat/rooms/${currentRoom._id}/messages/${message._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete message');
      }

      const result = await response.json();
      console.log('✅ Message deleted successfully:', result);
      
      // Update message in local state to show as deleted (soft delete)
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === message._id 
            ? { ...msg, isDeleted: true, deletedAt: result.deletedAt, deletedBy: user._id || user.id }
            : msg
        )
      );
      
      showNotification('Message deleted successfully!', 'success');
      
    } catch (error) {
      console.error('❌ Error deleting message:', error);
      showNotification(error.message || 'Failed to delete message. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Group messages with their replies
  const groupMessagesWithReplies = (messages) => {
    const messageMap = new Map();
    const rootMessages = [];
    
    // First pass: create a map of all messages
    messages.forEach(message => {
      messageMap.set(message._id, { ...message, replies: [] });
    });
    
    // Second pass: organize messages into parent-child relationships
    messages.forEach(message => {
      if (message.replyTo) {
        const parentMessage = messageMap.get(message.replyTo);
        if (parentMessage) {
          parentMessage.replies.push(messageMap.get(message._id));
        } else {
          // If parent message not found, treat as root message
          rootMessages.push(messageMap.get(message._id));
        }
      } else {
        rootMessages.push(messageMap.get(message._id));
      }
    });
    
    // Sort replies by creation time
    const sortMessages = (msgs) => {
      return msgs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    };
    
    // Sort all messages and their replies
    rootMessages.forEach(msg => {
      msg.replies = sortMessages(msg.replies);
    });
    
    return sortMessages(rootMessages);
  };

  // Component to render a message with its replies
  const renderMessageWithReplies = (message, index, filteredMessages) => {
    const isOwnMessage = message.sender?._id === (user._id || user.id);
    const showDateHeader = index === 0 || 
      new Date(message.createdAt).toDateString() !== 
      new Date(filteredMessages[index - 1].createdAt).toDateString();
    
    return (
      <React.Fragment key={message._id || index}>
        {showDateHeader && (
          <div className="message-date-header">
            <div className="date-header-content">
              {new Date(message.createdAt).toDateString() === new Date().toDateString() ? (
                'Today'
              ) : (
                new Date(message.createdAt).toLocaleDateString()
              )}
            </div>
          </div>
        )}
        
        {/* Main Message */}
        <div 
          className={`message ${message.isSystemMessage ? 'system' : (isOwnMessage ? 'own' : 'other')}`}
          data-message-id={message._id}
        >
          <div className="message-content">
            {!isOwnMessage && !message.isSystemMessage && (
              <div className="message-sender">
                {message.sender?.name || 'Unknown'}
              </div>
            )}
            
            {message.meta?.isForwarded && (
              <div className="forwarded-indicator" title="Forwarded">
                <span className="forwarded-icon">↪</span>
                <span className="forwarded-text">Forwarded</span>
              </div>
            )}

            
            {/* Reply Context */}
            {message.replyTo && (() => {
              // Find the original message to get the sender info
              const originalMessage = messages.find(msg => msg._id === message.replyTo);
              const senderName = originalMessage?.sender?.name || 
                                message.replyTo.sender?.name || 
                                (typeof message.replyTo === 'string' ? 'Unknown' : 'Unknown');
              const messageContent = originalMessage?.content || 
                                   (typeof message.replyTo === 'object' ? message.replyTo.content : '');
              const messageAttachments = originalMessage?.attachments || 
                                       (typeof message.replyTo === 'object' ? message.replyTo.attachments : []);
              
              const handleReplyContextClick = () => {
                const originalMessageElement = document.querySelector(`[data-message-id="${message.replyTo}"]`);
                if (originalMessageElement) {
                  // Add highlight effect
                  originalMessageElement.classList.add('message-highlight');
                  
                  // Scroll to the original message
                  originalMessageElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                  });
                  
                  // Remove highlight after animation
                  setTimeout(() => {
                    originalMessageElement.classList.remove('message-highlight');
                  }, 2000);
                }
              };
              
              return (
                <div 
                  className="message-reply-context clickable-reply-context"
                  onClick={handleReplyContextClick}
                  title="Click to jump to original message"
                >
                  <div className="reply-line"></div>
                  <div className="reply-info">
                    <span className="reply-sender">
                      {senderName}
                    </span>
                    <span className="reply-content">
                      {messageContent ? (
                        messageContent.length > 50 
                          ? messageContent.substring(0, 50) + '...' 
                          : messageContent
                      ) : (
                        messageAttachments && messageAttachments.length > 0 
                          ? `📎 ${messageAttachments.length} attachment${messageAttachments.length > 1 ? 's' : ''}`
                          : 'Message'
                      )}
                    </span>
                  </div>
                </div>
              );
            })()}
            
            <div className="message-text">
              {(() => {
                // Check if message is deleted
                if (message.isDeleted) {
                  return (
                    <span className="deleted-message">
                      <em>🗑️ This message was deleted</em>
                    </span>
                  );
                }
                
                // Check if message is a system message
                if (message.isSystemMessage) {
                  return (
                    <span className="system-message">
                      <em>{message.content}</em>
                    </span>
                  );
                }
                
                console.log('🔍 Rendering message:', message.content);
                console.log('🔍 Users available for rendering:', users);
                console.log('🔍 Users length:', users.length);
                console.log('🔍 Users loaded:', usersLoaded);
                
                // If users are not loaded yet, render plain text
                if (!usersLoaded || users.length === 0) {
                  console.log('🔍 Users not loaded, rendering plain text');
                  return <span>{message.content}</span>;
                }
                
                const slug = (s='') => s.toLowerCase().replace(/[\W_]+/g, '');
                const tokens = message.content.split(/\s+/);
                const out = [];

                // Precompute user slugs for fast matching
                const userIndex = users.map(u => ({
                  user: u,
                  nameSlug: slug(u.name || ''),
                  emailSlug: slug((u.email || '').split('@')[0]),
                }));
                
                console.log('🔍 User index created:', userIndex);

                for (let i = 0; i < tokens.length; i++) {
                  const t = tokens[i];

                  if (t.startsWith('@')) {
                    console.log('🔍 Processing @ token:', t);
                    // Try to greedily expand across the next few tokens to match a full name
                    let consumed = 0;
                    let candidate = t.slice(1);             // drop '@'
                    let bestMatch = null;
                    let bestConsumed = 0;

                    // look ahead up to 4 tokens to assemble "BD Dept" etc.
                    for (let j = 0; j <= 4 && i + j < tokens.length; j++) {
                      if (j > 0) candidate += ' ' + tokens[i + j];
                      const candSlug = slug(candidate);

                      // find exact or prefix match on slugs
                      const match = userIndex.find(x =>
                        x.nameSlug === candSlug ||
                        x.nameSlug.startsWith(candSlug) ||
                        x.emailSlug === candSlug ||
                        x.emailSlug.startsWith(candSlug)
                      );

                      if (match) {
                        bestMatch = match.user;
                        bestConsumed = j; // how many extra tokens we cover
                      }
                    }

                    if (bestMatch) {
                      console.log('🔍 Found match for mention:', bestMatch);
                      out.push(
                        <span
                          key={`m-${i}`}
                          className="mention-tag clickable"
                          onClick={() => handleMentionClick(bestMatch)}
                          title={`Click to open DM with ${bestMatch.name}`}
                          style={{ cursor: 'pointer' }}
                        >
                          @{bestMatch.name}
                        </span>
                        );
                      i += bestConsumed; // consume the trailing parts ("Dept") so they don't render
                      continue;
                    } else {
                      console.log('🔍 No match found for mention:', t);
                    }

                    // Fallback: no match—render as plain text mention
                    out.push(<span key={`t-${i}`}>{t}{i < tokens.length - 1 ? ' ' : ''}</span>);
                  } else {
                    out.push(<span key={`t-${i}`}>{t}{i < tokens.length - 1 ? ' ' : ''}</span>);
                  }
                }

                return out;
              })()}
              
              {/* Inline edited indicator removed per UX: show only in timestamp row */}
            </div>
            
            {/* Message Editor */}
            {editingMessageId === message._id && (
              <MessageEditor
                message={message}
                roomId={currentRoom._id}
                token={token}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                currentUser={user}
              />
            )}
            

            
            {/* File Attachments - Hidden for deleted messages */}
            {!message.isDeleted && message.attachments && message.attachments.length > 0 && (
              <div className="file-attachments">
                {message.attachments.map((attachment, attIndex) => (
                  <div key={attIndex} className="file-attachment">
                    {getAttachmentPreview(attachment)}
                    <div className="file-attachment-info">
                      <div className="file-attachment-name">{attachment.originalName || attachment.name}</div>
                      <div className="file-attachment-size">
                        {formatFileSize(attachment.size)}
                      </div>
                    </div>
                    {getAttachmentActionButton(attachment)}
                  </div>
                ))}
              </div>
            )}

            <div className="message-time">
              <span className="timestamp-text">{formatChatTime(message.createdAt)}</span>
              {(message.editedAt || (message.meta && Array.isArray(message.meta.editHistory) && message.meta.editHistory.length > 0)) && (
                <>
                  <span className="edited-sep" aria-hidden="true">•</span>
                  <span className="message-edited-inline" title={`Edited ${new Date(message.editedAt).toLocaleString()}`}>Edited</span>
                </>
              )}
              <DeliveryStatus 
                message={message}
                currentUserId={user._id || user.id}
                isOwnMessage={isOwnMessage}
              />
            </div>
            
            {/* Message Actions - Hidden for deleted messages */}
            {!message.isDeleted && (() => {
              console.log('🔍 [DEBUG] Current room details:', {
                roomId: currentRoom?._id,
                roomType: currentRoom?.type,
                roomName: currentRoom?.name,
                isAdmin: isRoomAdmin,
                isDirect: currentRoom?.type === 'direct'
              });
              return null;
            })()}
            {!message.isDeleted && (
              <MessageActions
                message={message}
                onReply={handleReply}
                onReplyInThread={handleReplyInThread}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                onForwardMessage={handleForwardMessage}
                onPinMessage={handlePinMessage}
                currentUser={user}
                threadCount={threadCounts[message._id]?.replyCount || 0}
                roomId={currentRoom?._id}
                roomType={currentRoom?.type}
                token={token}
                isAdmin={isRoomAdmin}
                isPinned={pinnedMessageIds.has(message._id)}
              />
            )}
          </div>
        </div>

        {/* Render Replies */}
        {message.replies && message.replies.length > 0 && (
          <div className="message-replies">
            {message.replies.map((reply, replyIndex) => (
              <div 
                key={reply._id || replyIndex}
                className={`message reply ${reply.sender?._id === (user._id || user.id) ? 'own' : 'other'}`}
                data-message-id={reply._id}
              >
                <div className="message-content">
                  {reply.sender?._id !== (user._id || user.id) && (
                    <div className="message-sender">
                      {reply.sender?.name || 'Unknown'}
                    </div>
                  )}
                  
                  {reply.meta?.isForwarded && (
                    <div className="forwarded-indicator" title="Forwarded">
                      <span className="forwarded-icon">↪</span>
                      <span className="forwarded-text">Forwarded</span>
                    </div>
                  )}

                  <div className="message-text">
                    {reply.isDeleted ? (
                      <span className="deleted-message">
                        <em>🗑️ This message was deleted</em>
                      </span>
                    ) : (
                      reply.content
                    )}
                  </div>
                  
                  {/* File Attachments for replies - Hidden for deleted messages */}
                  {!reply.isDeleted && reply.attachments && reply.attachments.length > 0 && (
                    <div className="file-attachments">
                      {reply.attachments.map((attachment, attIndex) => (
                        <div key={attIndex} className="file-attachment">
                          {getAttachmentPreview(attachment)}
                          <div className="file-attachment-info">
                            <div className="file-attachment-name">{attachment.originalName || attachment.name}</div>
                            <div className="file-attachment-size">
                              {formatFileSize(attachment.size)}
                            </div>
                          </div>
                          {getAttachmentActionButton(attachment)}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="message-time">
                    <span className="timestamp-text">{formatChatTime(reply.createdAt)}</span>
                    {(reply.editedAt || (reply.meta && Array.isArray(reply.meta.editHistory) && reply.meta.editHistory.length > 0)) && (
                      <>
                        <span className="edited-sep" aria-hidden="true">•</span>
                        <span className="message-edited-inline" title={`Edited ${new Date(reply.editedAt).toLocaleString()}`}>Edited</span>
                      </>
                    )}
                    <DeliveryStatus 
                      message={reply}
                      currentUserId={user._id || user.id}
                      isOwnMessage={reply.sender?._id === (user._id || user.id)}
                    />
                  </div>
                  
                  {/* Message Actions for replies - Hidden for deleted messages */}
                  {!reply.isDeleted && (
                    <MessageActions
                      message={reply}
                      onReply={handleReply}
                      onReplyInThread={handleReplyInThread}
                      onEditMessage={handleEditMessage}
                      onDeleteMessage={handleDeleteMessage}
                      onForwardMessage={handleForwardMessage}
                      onPinMessage={handlePinMessage}
                      currentUser={user}
                      threadCount={threadCounts[reply._id]?.replyCount || 0}
                      roomId={currentRoom?._id}
                      roomType={currentRoom?.type}
                      token={token}
                      isAdmin={isRoomAdmin}
                      isPinned={pinnedMessageIds.has(reply._id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const markRoomAsRead = async (roomId) => {
    try {
      const response = await fetch(`${API_BASE}/api/chat/rooms/${roomId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Update both unread counts to 0 for this room
        const normalizedRoomId = normalizeRoomId(roomId);
        const newCounts = {
          ...unreadMessages,
          [normalizedRoomId]: 0
        };
        
        // Update global context immediately
        updateUnreadMessages(newCounts);
        
        console.log('✅ Room marked as read:', roomId);
      }
    } catch (error) {
      console.error('Error marking room as read:', error);
    }
  };

  const updateUnreadCount = (roomId, count) => {
    const normalizedRoomId = normalizeRoomId(roomId);
    const newCounts = {
      ...unreadMessages,
      [normalizedRoomId]: count
    };
    updateUnreadMessages(newCounts);
  };

  // Mark messages as read when scrolling to bottom
  const markMessagesAsRead = useCallback(async () => {
    if (!currentRoom || !socket || !isSocketAuthed) return;
    
    try {
      // Get the latest message sequence in the current room
      const roomMessages = messages.filter(msg => msg.roomId === currentRoom._id);
      const latestMessage = roomMessages
        .sort((a, b) => (b.seq || 0) - (a.seq || 0))[0];
      
      if (latestMessage && latestMessage.seq) {
        // Emit socket event to mark messages as read
        socket.emit('messages:read', {
          roomId: currentRoom._id,
          beforeSeq: latestMessage.seq
        });
        
        // Also call REST API for reliability
        const response = await fetch(`${API_BASE}/api/chat/rooms/${currentRoom._id}/read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            lastSeenSeq: latestMessage.seq
          })
        });
        
        if (response.ok) {
          console.log('✅ Messages marked as read via REST API');
        }
      }
      
      // Clear unread counts for this room
      const normalizedRoomId = normalizeRoomId(currentRoom._id);
      const newCounts = {
        ...unreadMessages,
        [normalizedRoomId]: 0
      };
      
      // Update global context immediately
      updateUnreadMessages(newCounts);
    } catch (error) {
      console.error('❌ Error marking messages as read:', error);
    }
  }, [currentRoom, messages, socket, isSocketAuthed, API_BASE]);

  // Throttled function to mark messages as read
  const throttledMarkAsRead = useCallback(
    throttle(markMessagesAsRead, 1000), // Throttle to once per second
    [markMessagesAsRead]
  );

  // Add scroll event listener to mark messages as read
  useEffect(() => {
    const messagesContainer = document.querySelector('.messages-container');
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      
      if (isAtBottom) {
        throttledMarkAsRead();
      }
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    
    // Mark as read when component mounts or room changes
    throttledMarkAsRead();

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
    };
  }, [currentRoom, throttledMarkAsRead]);

  // Cleanup typing indicators when room changes or component unmounts
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (socket && isSocketAuthed && currentRoom && isTyping) {
        socket.emit('typing:stop', { roomId: currentRoom._id });
      }
    };
  }, [currentRoom, socket, isSocketAuthed, isTyping]);

  // Clear typing indicators when room changes
  useEffect(() => {
    // Don't clear all typing, just reset our own typing state
    setIsTyping(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [currentRoom]);



  const handleMentionClick = useCallback(async (selectedUser) => {
    try {
      console.log('🔄 Opening DM for user:', selectedUser);
      // Check if DM already exists
      const existingDM = rooms.find(room => 
        room.type === 'direct' && 
        room.members?.some(member => member._id === selectedUser._id)
      );
      
      if (existingDM) {
        console.log('✅ Found existing DM:', existingDM);
        setCurrentRoom(existingDM);
        fetchMessages(existingDM._id);
        markRoomAsRead(existingDM._id);
        
        if (socket) {
          socket.emit('room:join', { roomId: existingDM._id });
        }
        
        // Close mentions dropdown
        setShowMentions(false);
        return;
      } else {
        // Create a new DM room
        try {
          const dmRoomData = {
            name: 'Direct Message', // Generic name, will be displayed as "DM with [other person]"
            description: `Direct message between users`,
            type: 'direct',
            creator: user.id, // Add creator field so backend can set as admin
            members: [selectedUser._id, user.id],
            isPrivate: true
          };
          
          console.log('📝 Creating new DM room:', dmRoomData);
      
          const response = await fetch(`${API_BASE}/api/chat/rooms`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(dmRoomData)
          });
      
          if (response.ok) {
            const result = await response.json();
            const newDM = result.room;
            console.log('✅ New DM created:', newDM);
            
            // Add the new DM to the rooms list
            setRooms(prev => [newDM, ...prev]);
            
            setCurrentRoom(newDM);
            setMessages([]);
            
            if (socket) {
              socket.emit('room:join', { roomId: newDM._id });
            }
            
            // Close mentions dropdown
            setShowMentions(false);
          } else {
            console.error('❌ Failed to create DM room:', response.status);
            alert('Failed to create DM room. Please try again.');
          }
        } catch (error) {
          console.error('Error starting DM:', error);
          alert('Error starting DM. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Error creating DM:', error);
      alert('Error starting DM. Please try again.');
    }
  }, [rooms, user, token, socket, fetchMessages]);

  const handleFileUpload = async (files) => {
    try {
      console.log('📎 Processing file uploads:', files);
      
      const processedFiles = Array.from(files).map(file => ({
        file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }));
      
      setAttachments(prev => [...prev, ...processedFiles]);
      setShowFileUpload(false);
      
      console.log('✅ Files processed and added to attachments');
    } catch (error) {
      console.error('❌ Error processing files:', error);
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => {
      const attachment = prev.find(att => att.id === attachmentId);
      if (attachment && attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(att => att.id !== attachmentId);
    });
  };

  const uploadFiles = async (files) => {
    try {
      console.log('📎 Starting file upload for', files.length, 'files');
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file.file);
      });

      const response = await fetch(`${API_BASE}/api/upload/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Files uploaded successfully:', result.files);
        
        // Map the uploaded files to the format expected by the message
        return result.files.map(uploadedFile => ({
          filename: uploadedFile.filename,
          originalName: uploadedFile.originalName,
          path: uploadedFile.path,
          size: uploadedFile.size,
          mimetype: uploadedFile.mimetype,
          uploadedAt: new Date()
        }));
      } else {
        const error = await response.json();
        console.error('❌ File upload failed:', error);
        throw new Error(error.message || 'File upload failed');
      }
    } catch (error) {
      console.error('❌ Error uploading files:', error);
      throw error;
    }
  };

  // Attachment menu functions
  const handleDocumentUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx';
    input.onchange = (e) => handleFileUpload(e.target.files);
    input.click();
    setShowAttachmentMenu(false);
  };

  const handleCameraCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => handleFileUpload(e.target.files);
    input.click();
    setShowAttachmentMenu(false);
  };

  const handlePhotoGallery = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,video/*';
    input.onchange = (e) => handleFileUpload(e.target.files);
    input.click();
    setShowAttachmentMenu(false);
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer OGG (better server compatibility); fallback to WebM if not supported
      let mimeType = '';
      if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      }
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const usedMime = mimeType || 'audio/webm';
        const extension = usedMime.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunks, { type: usedMime });
        const filelike = new File([blob], `voice-message-${Date.now()}.${extension}`, { type: usedMime });
        handleFileUpload([filelike]);
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      recorder.start();

      // Start recording timer
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Store timer reference
      recorder.timer = timer;
    } catch (error) {
      console.error('Error starting audio recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
    setShowAttachmentMenu(false);
  };

  const stopAudioRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      clearInterval(mediaRecorder.timer);
      setIsRecording(false);
      setRecordingTime(0);
      setMediaRecorder(null);
    }
  };

  const handleLocationShare = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const locationMessage = `📍 Location: https://maps.google.com/?q=${latitude},${longitude}`;
          setNewMessage(locationMessage);
          setShowAttachmentMenu(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Could not get your location. Please check permissions.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
    setShowAttachmentMenu(false);
  };

  const fetchAvailableContacts = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const users = await response.json();
        setAvailableContacts(users.filter(u => u._id !== (user._id || user.id)));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleContactSelect = (contact) => {
    let contactMessage = `👤 Contact: ${contact.name}`;
    if (contact.email) contactMessage += `\n✉️ ${contact.email}`;
    if (contact.phone) contactMessage += `\n📞 ${contact.phone}`;
    if (contact.department) contactMessage += `\n🏢 ${contact.department}`;
    
    setNewMessage(contactMessage);
    setShowContactPicker(false);
    setShowAttachmentMenu(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleRoomSelect = (room) => {
    setCurrentRoom(room);
    fetchMessages(room._id);
    
    // Clear unread counts for the selected room
    const normalizedRoomId = normalizeRoomId(room._id);
    const newCounts = {
      ...unreadMessages,
      [normalizedRoomId]: 0
    };
    
    // Update global context immediately
    updateUnreadMessages(newCounts);
    
    // Mark room as read when selected
    markRoomAsRead(room._id);
    
    if (socket) {
      socket.emit('room:join', { roomId: room._id });
    }
  };

  const handleUserSelect = async (selectedUser) => {
    try {
      // Check if DM already exists
      const existingDM = rooms.find(room => 
        room.type === 'direct' && 
        room.members.some(member => member._id === selectedUser._id)
      );
      
      if (existingDM) {
        setCurrentRoom(existingDM);
        fetchMessages(existingDM._id);
        
        // Mark DM as read when selected
        markRoomAsRead(existingDM._id);
        
        if (socket) {
          socket.emit('room:join', { roomId: existingDM._id });
        }
      } else {
        // Create a new DM room in the backend
        const dmRoomData = {
          name: 'Direct Message', // Generic name, will be displayed as "DM with [other person]"
          description: `Direct message between users`,
          type: 'direct',
          creator: user.id, // Add creator field so backend can set as admin
          members: [selectedUser._id, user.id], // Include both users
          isPrivate: true
        };

        const response = await fetch(`${API_BASE}/api/chat/rooms`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dmRoomData)
        });

        if (response.ok) {
          const result = await response.json();
          const newDM = result.room;
          
          // Add the new DM to the rooms list
          setRooms(prev => [newDM, ...prev]);
          
          setCurrentRoom(newDM);
          setMessages([]);
          
          if (socket) {
            socket.emit('room:join', { roomId: newDM._id });
          }
        } else {
          console.error('Failed to create DM room:', response.status);
          alert('Failed to create DM room. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error starting DM:', error);
      alert('Error starting DM. Please try again.');
    }
  };


  const handleCreateRoom = (newRoom) => {
    setRooms(prev => [newRoom, ...prev]);
    setCurrentRoom(newRoom);
    setShowRoomManagement(false);
    setShowCreateRoom(false);
  };

  const handleRoomDelete = (deletedRoomId) => {
    console.log('🗑️ Room deleted:', deletedRoomId);
    
    // Remove the room from the rooms list
    setRooms(prev => prev.filter(room => room._id !== deletedRoomId));
    
    // If the deleted room was the current room, clear it
    if (currentRoom?._id === deletedRoomId) {
      setCurrentRoom(null);
      setMessages([]);
    }
    
    // Close the room management modal
    setShowRoomManagement(false);
    setSelectedRoom(null);
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type, timestamp: Date.now() });
    // Auto-hide quickly (1 second)
    setTimeout(() => setNotification(null), 1000);
  };

  const handleRoomUpdate = (updatedRoom) => {
    console.log('🔄 handleRoomUpdate called with:', {
      roomId: updatedRoom._id,
      roomName: updatedRoom.name,
      memberCount: updatedRoom.members?.length,
      members: updatedRoom.members?.map(m => m.name || m.email),
      currentRoomId: currentRoom?._id,
      isCurrentRoom: currentRoom?._id === updatedRoom._id,
      fullRoom: updatedRoom
    });
    
    setRooms(prev => {
      console.log('🔄 Updating rooms in handleRoomUpdate');
      console.log('🔄 Previous rooms:', prev.map(r => ({ id: r._id, memberCount: r.members?.length })));
      const updatedRooms = prev.map(room => 
        room._id === updatedRoom._id ? updatedRoom : room
      );
      console.log('🔄 Updated rooms:', updatedRooms.map(r => ({ id: r._id, memberCount: r.members?.length })));
      return updatedRooms;
    });
    
    if (currentRoom?._id === updatedRoom._id) {
      console.log('🔄 Updating current room and members');
      setCurrentRoom(updatedRoom);
      
      // Update room members for the current room
      if (updatedRoom.members) {
        console.log('🔄 Setting room members:', updatedRoom.members.length, updatedRoom.members.map(m => m.name || m.email));
        setRoomMembers(updatedRoom.members);
      }
      
      // If messages were cleared (room has no lastMessage), clear the messages state
      if (!updatedRoom.lastMessage) {
        setMessages([]);
        console.log('🧹 Messages cleared for room:', updatedRoom._id);
        showNotification('All messages have been cleared from this room', 'success');
      }
    }

    // If Room Management modal is open for this room, update it in place
    if (selectedRoom?._id === updatedRoom._id) {
      console.log('🔄 Updating selectedRoom for RoomManagement modal');
      // Add a timestamp to force React to recognize the change
      const updatedRoomWithTimestamp = {
        ...updatedRoom,
        _lastUpdated: Date.now()
      };
      setSelectedRoom(updatedRoomWithTimestamp);
      // Increment counter to force RoomManagement remount
      setRoomUpdateCounter(prev => prev + 1);
    }
  };

  // Mobile-specific functions
  const handleMobileRoomSelect = (room) => {
    setCurrentRoom(room);
    
    // Clear unread counts for the selected room
    const normalizedRoomId = normalizeRoomId(room._id);
    const newCounts = {
      ...unreadMessages,
      [normalizedRoomId]: 0
    };
    
    // Update global context immediately
    updateUnreadMessages(newCounts);
    
    if (isMobile) {
      setShowMobileSidebar(false);
      setMobileView('chat');
    }
  };

  const handleMobileUserSelect = (user) => {
    // Handle direct message creation
    const existingDM = rooms.find(room => 
      room.type === 'direct' && 
      room.members.some(member => member._id === user._id)
    );
    
    if (existingDM) {
      handleMobileRoomSelect(existingDM);
    } else {
      // Create new DM
      createDirectMessage(user);
      if (isMobile) {
        setShowMobileSidebar(false);
        setMobileView('chat');
      }
    }
  };

  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
    setMobileView(showMobileSidebar ? 'chat' : 'sidebar');
  };

  const handleMobileSearch = () => {
    setMobileView('search');
    setShowSearch(true);
  };

  // Mobile gesture handling
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && !showMobileSidebar) {
      // Swipe left to open sidebar
      setShowMobileSidebar(true);
      setMobileView('sidebar');
      setSwipeDirection('left');
    } else if (isRightSwipe && showMobileSidebar) {
      // Swipe right to close sidebar
      setShowMobileSidebar(false);
      setMobileView('chat');
      setSwipeDirection('right');
    }

    // Reset touch states
    setTimeout(() => {
      setTouchStart(null);
      setTouchEnd(null);
      setSwipeDirection(null);
    }, 300);
  };

  // Mobile keyboard handling
  const handleMobileKeyboard = () => {
    if (!isMobile) return;
    
    // Adjust viewport when keyboard appears
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }
  };

  // Mobile scroll to bottom with momentum
  const scrollToBottomMobile = () => {
    if (!isMobile) return;
    
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Mobile performance optimizations
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Debounced mobile functions
  const debouncedMobileRoomSelect = debounce(handleMobileRoomSelect, 100);
  const debouncedMobileUserSelect = debounce(handleMobileUserSelect, 100);
  const debouncedToggleSidebar = debounce(toggleMobileSidebar, 150);

  // Mobile viewport optimization
  const optimizeMobileViewport = () => {
    if (!isMobile) return;
    
    // Prevent zoom on input focus
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }
    
    // Optimize touch events
    document.body.style.touchAction = 'pan-x pan-y';
    document.body.style.webkitOverflowScrolling = 'touch';
  };

  // Mobile memory optimization
  const optimizeMobileMemory = () => {
    if (!isMobile) return;
    
    // Limit message history for mobile
    if (messages.length > 100) {
      setMessages(prev => prev.slice(-100));
    }
    
    // Clear unused state
    if (showMobileSidebar && mobileView === 'chat') {
      setShowMobileSidebar(false);
    }
  };

  // Mobile detection effect
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowMobileSidebar(false);
        setMobileView('chat');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile keyboard detection
  useEffect(() => {
    if (!isMobile) return;

    const handleResize = () => {
      const isKeyboardOpen = window.innerHeight < window.screen.height * 0.75;
      const inputContainer = document.querySelector('.message-input-container');
      
      if (inputContainer) {
        if (isKeyboardOpen) {
          inputContainer.classList.add('keyboard-open');
        } else {
          inputContainer.classList.remove('keyboard-open');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Mobile viewport optimization effect
  useEffect(() => {
    if (isMobile) {
      optimizeMobileViewport();
    }
  }, [isMobile]);

  // Mobile memory optimization effect
  useEffect(() => {
    if (isMobile) {
      optimizeMobileMemory();
    }
  }, [messages, showMobileSidebar, mobileView, isMobile]);

  useEffect(() => {
    if (token && user) {
      fetchRooms();
      fetchUsers();
      // Note: unread counts are now handled by ChatContext globally
    }
  }, [token, user, fetchRooms, fetchUsers]);


  useEffect(() => {
    if (token && user && currentRoom?._id) {
      const roomId = currentRoom._id;
      fetchMessages(roomId);
    }
  }, [token, user, currentRoom, fetchMessages]);

  useEffect(() => {
    if (isMobile) {
      scrollToBottomMobile();
    } else {
      scrollToBottom();
    }
  }, [messages, isMobile]);

  useEffect(() => {
    if (currentRoom?._id) {
      const roomId = currentRoom._id;
      fetchMessages(roomId);
    }
  }, [currentRoom, token, fetchMessages]);

  if (loading) {
    return (
      <div className="chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-error">
        <p>❌ {error}</p>
        <button onClick={fetchRooms} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div 
      className={`chat-container ${isMobile ? 'mobile' : ''} ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Mobile Header */}
      {isMobile && currentRoom && (
        <div className="mobile-header">
          <button 
            className="mobile-back-btn"
            onClick={() => {
              setCurrentRoom(null);
              setShowMobileSidebar(false);
            }}
            aria-label="Back to chat list"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </button>
          
          <div className="mobile-header-info">
            {currentRoom ? (
              <>
                <div className="mobile-header-avatar">
                  {currentRoom?.type === 'direct' ? (() => {
                    const otherMember = currentRoom.members?.find(member => member._id !== (user._id || user.id));
                    const name = otherMember?.name || 'Unknown';
                    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                  })() : (() => {
                    const name = currentRoom.name || 'Room';
                    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                  })()}
                </div>
                <div className="mobile-header-text">
                  <h2 className="mobile-room-name">
                    {currentRoom.type === 'direct' 
                      ? (() => {
                          const otherMember = currentRoom.members?.find(member => member._id !== (user._id || user.id));
                          return otherMember?.name || 'Unknown User';
                        })()
                      : currentRoom.name
                    }
                  </h2>
                  <p className="mobile-room-status">
                    {currentRoom.type === 'direct' ? 'online' : `${currentRoom.members?.length || 0} members`}
                  </p>
                </div>
              </>
            ) : (
              <>
                <h2>Chat</h2>
              </>
            )}
          </div>
          
          <div className="mobile-header-actions">
            <button 
              className="mobile-search-btn"
              onClick={handleMobileSearch}
              aria-label="Search"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
            
            {currentRoom && (
              <button 
                className="mobile-room-menu-btn"
                onClick={() => setShowRoomManagement(true)}
                aria-label="Room options"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && showMobileSidebar && (
        <div 
          className="mobile-sidebar-overlay"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}

      {/* Left Sidebar */}
      <div className={`chat-sidebar ${isMobile ? 'mobile' : ''} ${isMobile && showMobileSidebar ? 'show' : ''}`}>
        {/* Mobile Chat List Header */}
        {isMobile ? (
          <div className="mobile-chat-list-header">
            <div className="mobile-chat-list-title">
              <h2>Chats</h2>
              <div className={`mobile-connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                <div className="indicator-dot"></div>
                <span>{isConnected ? 'Online' : 'Offline'}</span>
              </div>
            </div>
            <button 
              className="mobile-new-chat-btn"
              onClick={() => setShowStartNewChat(true)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
            </button>
          </div>
        ) : (
          /* Desktop Chat Header */
          <div className="chat-header">
            <div className="chat-header-left">
             <h2>Chat</h2>
            </div>
            <div className="chat-header-right">
              <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                {isConnected ? 'ONLINE' : 'OFFLINE'}
              </div>
            </div>
           </div>
        )}

        {/* Tab Navigation */}
        {isMobile ? (
          <div className="mobile-chat-tabs">
            <button 
              className={`mobile-tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button 
              className={`mobile-tab-button ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              Unread
            </button>
            <button 
              className={`mobile-tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              Favourites
            </button>
            <button 
              className={`mobile-tab-button ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              Groups
            </button>
          </div>
        ) : (
          <div className="whatsapp-tabs">
            <button 
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button 
              className={`tab-button ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              Unread
              {rooms.filter(room => getUnreadCount(unreadMessages, room._id) > 0).length > 0 && (
                <span className="unread-badge" style={{marginLeft: '8px'}}>
                  {rooms.filter(room => getUnreadCount(unreadMessages, room._id) > 0).length}
                </span>
              )}
            </button>
            <button 
              className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              Favourites
            </button>
            <button 
              className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              Groups
            </button>
          </div>
        )}

        {/* Search */}
        {isMobile ? (
          <div className="mobile-chat-search">
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mobile-search-input"
            />
          </div>
        ) : (
          <div className="whatsapp-search">
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        )}

        {/* Sidebar Content */}
        <div className="sidebar-content">
          <>
            {/* Mobile Chat List - No extra header needed */}
            {!isMobile && (
              <div className="tab-header">
                <h3>{activeTab === 'all' ? 'All Chats' : activeTab === 'unread' ? 'Unread' : activeTab === 'favorites' ? 'Favourites' : 'Groups'}</h3>
                <button 
                  className="create-new-btn"
                  onClick={() => activeTab === 'groups' ? setShowCreateRoom(true) : setShowStartNewChat(true)}
                  title={activeTab === 'groups' ? 'Start New Group' : 'Start New Chat'}
                >
                  <svg className="plus-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                </button>
              </div>
            )}
              
              <ul className={`dms-list ${isMobile ? 'mobile' : ''}`}>
                {filterChats(rooms).length === 0 ? (
                  <li className="no-dms">
                    <p>No chats available</p>
                    <p className="dm-help">{activeTab === 'favorites' ? 'No favorite chats yet' : activeTab === 'unread' ? 'No unread chats' : activeTab === 'groups' ? 'No groups available' : 'Start a chat with someone to get started'}</p>
                  </li>
                ) : (
                  filterChats(rooms)
                    .filter(room => {
                      const otherMember = room.members?.find(member => member._id !== (user._id || user.id));
                      return otherMember?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             room.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (room.lastMessage?.content && !room.lastMessage?.isDeleted && room.lastMessage.content.toLowerCase().includes(searchTerm.toLowerCase()));
                    })
                    .map(room => {
                      const otherMember = room.members?.find(member => member._id !== (user._id || user.id));
                      return (
                        <li
                          key={room._id}
                          className={`whatsapp-chat-item ${(currentRoom?._id === room._id) ? 'active' : ''}`}
                          onClick={() => isMobile ? handleMobileRoomSelect(room) : handleRoomSelect(room)}
                        >
                          <div className="whatsapp-avatar">
                            {room.type === 'direct' ? (
                              <svg className="dm-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            ) : (
                              <svg className="dm-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26V9H12v9h8z"/>
                              </svg>
                            )}
                          </div>
                          <div className="whatsapp-chat-info">
                            <div className="whatsapp-chat-header">
                              <div className="whatsapp-chat-name">
                                {favoriteChats.has(room._id) && <span className="whatsapp-favorite-star">★</span>}
                                {room.type === 'direct' 
                                  ? (otherMember?.name || room.name) 
                                  : room.name
                                }
                              </div>
                              <div className="whatsapp-chat-time">
                                {formatChatTime(room.lastMessage?.createdAt || room.createdAt)}
                              </div>
                            </div>
                            <div className="whatsapp-chat-preview">
                              {/* Show typing indicator if someone is typing in this room */}
                              {typingUsersByRoom[room._id] && Object.keys(typingUsersByRoom[room._id]).length > 0 ? (
                                <span className="typing-preview">
                                  <span className="typing-animation">⌨️</span>
                                  {Object.values(typingUsersByRoom[room._id])[0].name} is typing...
                                </span>
                              ) : (
                                <span>{formatMessagePreview(room, user._id || user.id)}</span>
                              )}
                              {(() => {
                                const count = getUnreadCount(unreadMessages, room._id);
                                return count > 0 ? (
                                  <span className="whatsapp-unread-badge">
                                    {count}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <div className="chat-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="favorite-btn"
                              onClick={() => toggleFavorite(room._id)}
                              title={favoriteChats.has(room._id) ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {favoriteChats.has(room._id) ? '★' : '☆'}
                            </button>
                          </div>
                        </li>
                      );
                    })
                )}
              </ul>
            </>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-main">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-left">
            {currentRoom && (
              <div className="chat-header-avatar">
                {currentRoom?.type === 'direct' ? (() => {
                  const otherMember = currentRoom.members?.find(member => member._id !== (user._id || user.id));
                  const name = otherMember?.name || 'Unknown';
                  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                })() : (() => {
                  const name = currentRoom.name || 'Room';
                  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                })()}
              </div>
            )}
            <div className="chat-header-info">
              <h2>
                {currentRoom?.type === 'direct' 
                  ? (() => {
                      const otherMember = currentRoom.members?.find(member => member._id !== (user._id || user.id));
                      return otherMember?.name || 'Unknown User';
                    })()
                  : currentRoom?.name || 'Select a room or user'
                }
              </h2>
              <p className="chat-subtitle">
                {currentRoom?.type === 'direct' ? 'online' : 
                 currentRoom?.type === 'private' ? 'Private Room' : 'Public Room'}
              </p>
            </div>
          </div>
          <div className="chat-header-right">
            {currentRoom && (
              <>
                <button 
                  className="pinned-messages-btn"
                  onClick={handleShowPinnedMessages}
                  title="View pinned messages"
                >
                  📌 Pinned
                </button>
                <button 
                  className="room-management-btn"
                  onClick={async () => {
                    if (currentRoom) {
                      try {
                      // Fetch detailed room information including creator and admins
                      const response = await fetch(`${API_BASE}/api/chat/rooms/${currentRoom._id}`, {
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        setSelectedRoom(data.room);
                        setShowRoomManagement(true);
                      } else {
                        console.error('Failed to fetch room details:', response.status);
                        // Fallback to current room data
                        setSelectedRoom(currentRoom);
                        setShowRoomManagement(true);
                      }
                    } catch (error) {
                      console.error('Error fetching room details:', error);
                      // Fallback to current room data
                      setSelectedRoom(currentRoom);
                      setShowRoomManagement(true);
                    }
                  }
                }}
                title={currentRoom ? "Manage Room" : "DM Settings"}
              >
                ⚙️
              </button>
              </>
            )}
          </div>
        </div>

        {/* Messages Container */}
        <div className="messages-container">
          {!currentRoom ? (
            isMobile ? (
              /* Mobile Chat List View */
              <div className="mobile-chat-list-view">
                <div className="mobile-chat-list-header">
                  <div className="mobile-chat-list-title">
                    <h2>Chats</h2>
                    <div className={`mobile-connection-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                      <div className="indicator-dot"></div>
                      <span>{isConnected ? 'Online' : 'Offline'}</span>
                    </div>
                  </div>
                  <button 
                    className="mobile-new-chat-btn"
                    onClick={() => setShowStartNewChat(true)}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </button>
                </div>

                <div className="mobile-chat-tabs">
                  <button 
                    className={`mobile-tab-button ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    All
                  </button>
                  <button 
                    className={`mobile-tab-button ${activeTab === 'unread' ? 'active' : ''}`}
                    onClick={() => setActiveTab('unread')}
                  >
                    Unread
                  </button>
                  <button 
                    className={`mobile-tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
                    onClick={() => setActiveTab('favorites')}
                  >
                    Favourites
                  </button>
                  <button 
                    className={`mobile-tab-button ${activeTab === 'groups' ? 'active' : ''}`}
                    onClick={() => setActiveTab('groups')}
                  >
                    Groups
                  </button>
                </div>

                <div className="mobile-chat-search">
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mobile-search-input"
                  />
                </div>

                <div className="mobile-chat-list-content">
                  <ul className="mobile-dms-list">
                    {filterChats(rooms).length === 0 ? (
                      <li className="mobile-no-dms">
                        <p>No chats available</p>
                        <p className="mobile-dm-help">{activeTab === 'favorites' ? 'No favorite chats yet' : activeTab === 'unread' ? 'No unread chats' : activeTab === 'groups' ? 'No groups available' : 'Start a chat with someone to get started'}</p>
                      </li>
                    ) : (
                      filterChats(rooms)
                        .filter(room => {
                            const otherMember = room.members?.find(member => member._id !== (user._id || user.id));
                            return otherMember?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                   room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                   (room.lastMessage?.content && !room.lastMessage?.isDeleted && room.lastMessage.content.toLowerCase().includes(searchTerm.toLowerCase()));
                          })
                          .map(room => {
                            const otherMember = room.members?.find(member => member._id !== (user._id || user.id));
                            return (
                              <li
                                key={room._id}
                                className="whatsapp-chat-item"
                                onClick={() => handleMobileRoomSelect(room)}
                              >
                                <div className="whatsapp-avatar">
                                  {room.type === 'direct' ? (
                                    <svg className="dm-icon" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  ) : (
                                    <svg className="dm-icon" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26V9H12v9h8z"/>
                                    </svg>
                                  )}
                                </div>
                                <div className="whatsapp-chat-info">
                                  <div className="whatsapp-chat-header">
                                    <div className="whatsapp-chat-name">
                                      {favoriteChats.has(room._id) && <span className="whatsapp-favorite-star">★</span>}
                                      {room.type === 'direct' 
                                        ? (otherMember?.name || room.name) 
                                        : room.name
                                      }
                                    </div>
                                    <div className="whatsapp-chat-time">
                                      {formatChatTime(room.lastMessage?.createdAt || room.createdAt)}
                                    </div>
                                  </div>
                                  <div className="whatsapp-chat-preview">
                                    {/* Show typing indicator if someone is typing in this room */}
                                    {typingUsersByRoom[room._id] && Object.keys(typingUsersByRoom[room._id]).length > 0 ? (
                                      <span className="typing-preview">
                                        <span className="typing-animation">⌨️</span>
                                        {Object.values(typingUsersByRoom[room._id])[0].name} is typing...
                                      </span>
                                    ) : (
                                      <span>{formatMessagePreview(room, user._id || user.id)}</span>
                                    )}
                                    {(() => {
                                      const count = getUnreadCount(unreadMessages, room._id);
                                      return count > 0 ? (
                                        <span className="whatsapp-unread-badge">
                                          {count}
                                        </span>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                                <div className="chat-actions" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="favorite-btn"
                                    onClick={() => toggleFavorite(room._id)}
                                    title={favoriteChats.has(room._id) ? 'Remove from favorites' : 'Add to favorites'}
                                  >
                                    {favoriteChats.has(room._id) ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })
                      )}
                    </ul>
                </div>
              </div>
            ) : (
              /* Desktop Welcome Screen */
              <div className="no-room-selected">
                <div className="no-room-content">
                  <div className="no-room-icon">
                    <svg viewBox="0 0 200 200" fill="currentColor" className="chat-illustration">
                      <circle cx="100" cy="100" r="80" fill="#e3f2fd" stroke="#2196f3" strokeWidth="2"/>
                      <circle cx="80" cy="85" r="8" fill="#2196f3"/>
                      <circle cx="120" cy="85" r="8" fill="#2196f3"/>
                      <path d="M70 120 Q100 160 130 120" stroke="#2196f3" strokeWidth="3" fill="none"/>
                    </svg>
                  </div>
                  
                  <h2>Select a room or user</h2>
                  <p>Choose a conversation from the sidebar to start chatting</p>

                  <div className="quick-actions">
                    <button 
                      className="action-button primary"
                      onClick={() => setShowStartNewChat(true)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="action-icon">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                      Start a new chat
                    </button>
                    
                    <button 
                      className="action-button secondary"
                      onClick={() => setShowCreateRoom(true)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="action-icon">
                        <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26V9H12v9h8z"/>
                      </svg>
                      Create a group
                    </button>
                    
                  </div>
                </div>
              </div>
            )
          ) : messages.length === 0 ? (
            <div className="no-messages">
              <div className="empty-chat-illustration">
                <svg viewBox="0 0 200 200" fill="currentColor" className="empty-chat-icon">
                  <circle cx="100" cy="100" r="80" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2"/>
                  <path d="M70 120 Q100 160 130 120" stroke="#d1d5db" strokeWidth="3" fill="none"/>
                  <circle cx="80" cy="85" r="6" fill="#d1d5db"/>
                  <circle cx="120" cy="85" r="6" fill="#d1d5db"/>
                </svg>
              </div>
              <h3>No messages yet</h3>
              <p>Start the conversation by typing a message below!</p>
              <div className="empty-chat-actions">
                <button 
                  className="action-button secondary"
                  onClick={() => setShowFileUpload(true)}
                >
                  📎 Share a file
                </button>
                <button 
                  className="action-button secondary"
                  onClick={() => {
                    const input = document.querySelector('.message-input');
                    if (input) input.focus();
                  }}
                >
                  💬 Type a message
                </button>
              </div>
            </div>
          ) : (
            (() => {
              const filteredMessages = messages.filter(message => message.roomId === currentRoom._id);
              const groupedMessages = groupMessagesWithReplies(filteredMessages);
              
              return groupedMessages.map((message, index) => 
                renderMessageWithReplies(message, index, groupedMessages)
              );
            })()
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Typing Indicators */}
        {currentRoom && typingUsersByRoom[currentRoom._id] && Object.keys(typingUsersByRoom[currentRoom._id]).length > 0 && (
          <div className="typing-indicators">
            <div className="typing-text">
              {Object.values(typingUsersByRoom[currentRoom._id]).map((user, index) => (
                <span key={user.name}>
                  {user.name} is typing
                  {index < Object.values(typingUsersByRoom[currentRoom._id]).length - 1 ? ', ' : ''}
                </span>
              ))}
              <span className="typing-dots">...</span>
            </div>
          </div>
        )}


        {/* Message Input */}
        {currentRoom && (
          <div className="message-input-container" style={{ position: 'relative' }}>
            {/* Reply Context */}
            {replyingTo && replyMessage && (
              <div className="reply-context">
                <div className="reply-context-header">
                  <span className="reply-label">Replying to {replyMessage.sender?.name || 'Unknown'}</span>
                  <button 
                    className="reply-cancel"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyMessage(null);
                    }}
                    title="Cancel reply"
                  >
                    ✕
                  </button>
                </div>
                <div className="reply-preview">
                  {replyMessage.content && (
                    <span className="reply-text">
                      {replyMessage.content.length > 100 
                        ? replyMessage.content.substring(0, 100) + '...' 
                        : replyMessage.content
                      }
                    </span>
                  )}
                  {replyMessage.attachments && replyMessage.attachments.length > 0 && (
                    <span className="reply-attachment">
                      📎 {replyMessage.attachments.length} attachment{replyMessage.attachments.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* File Upload Section */}
            {showFileUpload && (
              <div className="file-upload-section">
                <FileUpload 
                  onFilesSelected={handleFileUpload}
                  onClose={() => setShowFileUpload(false)}
                />
              </div>
            )}

            {/* Attachments Preview */}
            {attachments.length > 0 && (
              <div className="attachments-preview">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="attachment-item">
                    {attachment.preview ? (
                      <div className="attachment-preview-image">
                        <img 
                          src={attachment.preview} 
                          alt={attachment.name}
                          className="attachment-thumbnail"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="attachment-icon" style={{ display: 'none' }}>
                          {getFileTypeIcon(attachment.type, attachment.name)}
                        </div>
                      </div>
                    ) : (
                      <div className="attachment-icon">
                        {getFileTypeIcon(attachment.type, attachment.name)}
                      </div>
                    )}
                    <div className="attachment-details">
                      <div className="attachment-name">{attachment.name}</div>
                      <div className="attachment-size">
                        {formatFileSize(attachment.size)}
                      </div>
                    </div>
                    <button 
                      className="remove-attachment"
                      onClick={() => removeAttachment(attachment.id)}
                      title="Remove attachment"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Message Input Wrapper */}
            <div className="message-input-wrapper">
              <div className="input-controls">
                <div className="attachment-menu-container">
                  <button 
                    type="button"
                    className={`attachment-btn ${showAttachmentMenu ? 'active' : ''}`}
                    onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                    title="Attach files"
                  >
                    {showAttachmentMenu ? '✕' : '+'}
                  </button>
                  
                  {showAttachmentMenu && (
                    <div className="attachment-menu">
                      <div className="attachment-option" onClick={handleDocumentUpload}>
                        <div className="attachment-icon document">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </div>
                        <span>Document</span>
                      </div>
                      
                      {/* Camera and Gallery options removed per requirement */}
                      
                      {/* Audio, Location, and Contact options removed per requirement */}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Recording Indicator */}
              {isRecording && (
                <div className="recording-indicator">
                  <div className="recording-animation">🔴</div>
                  <span>Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}</span>
                  <button className="stop-recording-btn" onClick={stopAudioRecording}>
                    Stop
                  </button>
                </div>
              )}
          
              <textarea
                className="message-input"
                placeholder="Type a message... Use @ to mention users"
                value={newMessage}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewMessage(value);
                  
                  // Handle typing indicators
                  if (socket && isSocketAuthed && currentRoom) {
                    if (value.trim().length > 0 && !isTyping) {
                      setIsTyping(true);
                      socket.emit('typing:start', { roomId: currentRoom._id });
                    } else if (value.trim().length === 0 && isTyping) {
                      setIsTyping(false);
                      socket.emit('typing:stop', { roomId: currentRoom._id });
                    }
                    
                    // Clear existing timeout
                    if (typingTimeoutRef.current) {
                      clearTimeout(typingTimeoutRef.current);
                    }
                    
                    // Set timeout to stop typing after 2 seconds of inactivity
                    typingTimeoutRef.current = setTimeout(() => {
                      if (isTyping) {
                        setIsTyping(false);
                        socket.emit('typing:stop', { roomId: currentRoom._id });
                      }
                    }, 2000);
                  }
                  
                  // Skip @ mention detection if we're currently inserting a mention
                  if (isInsertingMention) {
                    console.log('🔍 Skipping @ detection - mention insertion in progress');
                    return;
                  }
                  
                  // Skip @ mention detection if users are not loaded
                if (!usersLoaded || users.length === 0) {
                    console.log('🔍 Skipping @ detection - users not loaded');
                    setShowMentions(false);
                    return;
                  }
                  
                  // Check for @ mentions
                  const lastAtSymbol = value.lastIndexOf('@');
                  console.log('🔍 @ detection:', { 
                    value, 
                    lastAtSymbol, 
                    usersLength: users.length, 
                    usersArray: users,
                    usersType: typeof users,
                    usersIsArray: Array.isArray(users),
                    usersLoaded
                  });
                  
                  if (lastAtSymbol !== -1) {
                    const afterAt = value.substring(lastAtSymbol + 1);
                    console.log('🔍 After @:', afterAt);
                    
                    // Check if we're still typing the mention (no space after @)
                    if (afterAt.length > 0 && !afterAt.includes(' ')) {
                      const mentionQuery = afterAt.toLowerCase();
                      console.log('🔍 Filtering users with query:', mentionQuery);
                      console.log('🔍 Users before filtering:', users);
                      const filteredUsers = users.filter(u => 
                        u._id !== (user._id || user.id) && (
                          u.name?.toLowerCase().includes(mentionQuery) || 
                          u.email?.toLowerCase().includes(mentionQuery)
                        )
                      );
                      console.log('🔍 Filtered users:', filteredUsers);
                      setMentionUsers(filteredUsers);
                      setShowMentions(true);
                      setMentionQuery(mentionQuery);
                      setCursorPosition(lastAtSymbol);
                    } else if (afterAt.length === 0) {
                      // Show all users when just @ is typed
                      const allUsers = users.filter(u => u._id !== (user._id || user.id));
                      console.log('🔍 Showing all users:', allUsers);
                      setMentionUsers(allUsers);
                      setShowMentions(true);
                      setMentionQuery('');
                      setCursorPosition(lastAtSymbol);
                    } else {
                      // Hide mentions if there's a space after @ (mention is complete)
                      setShowMentions(false);
                    }
                  } else {
                    setShowMentions(false);
                  }
                }}
                onSelect={(e) => {
                  // Update cursor position when user selects text or moves cursor
                  setCursorPosition(e.target.selectionStart);
                }}
                onKeyPress={handleKeyPress}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowMentions(false);
                  }
                }}
                rows="2"
                style={{ height: '40px' }}
              />

              
              {/* Mentions Dropdown */}
              {showMentions && (
                <div className="mentions-dropdown">
                  {mentionUsers.map((mentionUser) => (
                    <div
                      key={mentionUser._id}
                      className="mention-item"
                      onClick={() => {
                        // Completely remove everything from @ onwards and replace with clean mention
                        // This section is now handled by the simple approach below
                        
                        setIsInsertingMention(true);
                        
                        // SIMPLE APPROACH: Replace from @ to cursor and add space
                        const atIndex = newMessage.lastIndexOf('@');
                        if (atIndex !== -1) {
                          const before = newMessage.slice(0, atIndex);
                          const inserted = `@${mentionUser.name} `;
                          const nextValue = before + inserted;
                          
                          console.log('🔍 Simple mention insertion:', {
                            originalMessage: newMessage,
                            atIndex,
                            before,
                            inserted,
                            nextValue
                          });
                          
                          setIsInsertingMention(true);
                          setNewMessage(nextValue);
                          setShowMentions(false);
                          setMentionUsers([]);
                          
                          setTimeout(() => setIsInsertingMention(false), 0);
                          setTimeout(() => {
                            const textarea = document.querySelector('.message-input');
                            if (textarea) {
                              const pos = nextValue.length;
                              textarea.focus();
                              textarea.setSelectionRange(pos, pos);
                            }
                          }, 0);
                        }
                      }}
                    >
                      <div className="mention-avatar">
                        {mentionUser.name ? mentionUser.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="mention-info">
                      <div className="mention-name">{mentionUser.name || mentionUser.email}</div>
                        <div className="mention-email">{mentionUser.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button 
                className="send-button"
                onClick={sendMessage}
                disabled={!newMessage.trim() && attachments.length === 0}
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <div className={`chat-notification ${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Room Management Modal */}
      {showRoomManagement && (
        <RoomManagement
          key={`room-mgmt-${selectedRoom?._id}-${roomUpdateCounter}`}
          room={selectedRoom}
          onRoomUpdate={handleRoomUpdate}
          onCreateRoom={handleCreateRoom}
          onRoomDelete={handleRoomDelete}
          onClose={() => {
            setShowRoomManagement(false);
            setSelectedRoom(null);
          }}
        />
      )}

      {/* Create Room Modal - Use RoomManagement component */}
      {showCreateRoom && (
        <RoomManagement
          key="create-room"
          room={null}
          onRoomUpdate={handleRoomUpdate}
          onCreateRoom={handleCreateRoom}
          onClose={() => {
            setShowCreateRoom(false);
          }}
          isCreating={true}
        />
      )}


      {/* Start New Chat Modal */}
      {showStartNewChat && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Start New Chat</h3>
              <button 
                className="close-button"
                onClick={() => setShowStartNewChat(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select User to Chat With:</label>
                <div className="users-selection">
                  {users.length === 0 ? (
                    <p>No users available</p>
                  ) : (
                    users
                      .filter(user => 
                        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(user => (
                        <div
                          key={user._id}
                          className="user-selection-item"
                          onClick={() => {
                            if (isMobile) {
                              handleMobileUserSelect(user);
                            } else {
                              handleUserSelect(user);
                            }
                            setShowStartNewChat(false);
                          }}
                        >
                          <div className="user-avatar">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div className="user-info">
                            <div className="user-name">{user.name || user.email}</div>
                            <div className="user-role">{user.role}</div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pinned Messages */}
      {showPinnedMessages && currentRoom && (
        <PinnedMessages
          roomId={currentRoom._id}
          roomType={currentRoom.type}
          token={token}
          user={user}
          onClose={handleClosePinnedMessages}
          onUnpinMessage={handleUnpinFromPanel}
          socket={socket}
          isAdmin={isRoomAdmin}
        />
      )}

      {/* Forward Message Modal */}
      {showForwardModal && messageToForward && (
        <ForwardMessage
          message={messageToForward}
          token={token}
          user={user}
          onClose={handleCloseForwardModal}
          onForward={handleForwardComplete}
        />
      )}

      {/* Contact Picker Modal */}
      {showContactPicker && (
        <div className="modal-overlay" onClick={() => setShowContactPicker(false)}>
          <div className="contact-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact-picker-header">
              <h3>Select Contact</h3>
              <button 
                className="close-btn"
                onClick={() => setShowContactPicker(false)}
              >
                ✕
              </button>
            </div>
            <div className="contact-picker-content">
              {availableContacts.map(contact => (
                <div 
                  key={contact._id}
                  className="contact-item"
                  onClick={() => handleContactSelect(contact)}
                >
                  <div className="contact-avatar">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                    </svg>
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-details">
                      {contact.email && <span>{contact.email}</span>}
                      {contact.department && <span> • {contact.department}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Thread View - Disabled in favor of WhatsApp-style inline replies */}
      {/* {showThread && currentThreadMessage && (
        <ThreadView
          parentMessage={currentThreadMessage}
          roomId={currentRoom?._id}
          token={token}
          user={user}
          onClose={handleCloseThread}
          socket={socket}
        />
      )} */}
    </div>
  );
}
 